//latest
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import {
  billTypeHeaderSelectStyles,
  billTypeHeaderSelectWrapStyle,
  createdByHeaderSelectStyles,
  createdByHeaderSelectWrapStyle,
} from "../utils/billTypeHeaderSelectStyles";

import toast, { Toaster } from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import { node_imageBaseUrl, getUserData } from "./const";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  listBankAccounts,
  listBillTypes,
  createSale,
  listCustomerUsers,
  showCustomerUser,
  showProducts,
  showOrderQueue,
  listOrderQueue,
  isErpApiSuccess,
  getErpAuthState,
} from "../erpClient";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";
import {
  createSalesIdempotencyKey,
  isSalesApiSuccess,
  mapItemRateOn,
  mapLbrTypeForApi,
  ERP_SALES_LIST_INVALIDATE_EVENT,
} from "../utils/salesErpAdapter";
import {
  buildStandardPaymentLines,
  ensurePaymentRowInList,
  applyUrdLinkToPaymentRows,
} from "../utils/paymentLinesErpAdapter";
import {
  createSaleDraftKey,
  loadSaleDraft,
  saveSaleDraft,
  clearSaleDraft,
} from "../utils/saleDraftSession";

import { trackEvent, getTenantName } from "../services/analytics";
import { useLocation, useNavigate } from "react-router-dom";
import useFillSalesFromCustomOrder from "../utils/useFillSalesFromCustomOrder";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import OtherAmountModal from "../components/OtherAmountModal";
import {
  calculateOtherAmountAmount as calculateAmount,
  calculateOtherAmountLessWeight as calculateLessWeight,
  sumOtherAmountEntries,
  mapApiChargesToOtherAmountEntries,
  mapOtherAmountEntriesForUpdate,
  mapOtherAmountEntriesForCreate,
} from "../utils/otherAmountCalculations";
import CustomerSelection from "../components/CustomerSelection";
import { getMobileValidationError } from "../utils/mobileInput";
import { getEmailValidationError } from "../utils/emailInput";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import { autoRoundOff, roundLineTotalAmount } from "../utils/autoRoundOff";
import {
  mergeProductGroupOptions,
  searchProductGroupSelectOptions,
  fetchProductGroupOptionById,
} from "../utils/productGroupOptions";
import { numPool } from "../utils/productGroupPool";
import {
  resolveQueueDetailsId,
  findOrderQueueInResponse,
} from "../utils/queueNavigation";
import { normalizeProductStockShowResponse } from "../utils/productStockErpAdapter";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import PaymentSection, {
  PAYMENT_METHOD_LABELS,
} from "../components/PaymentSection";
import OldPurchaseModal from "../components/OldPurchaseModal";
import "../assets/css/theme-table-header.css";
import { commandBus } from "../desktop/commands/CommandBus";
import { desktopBus, DesktopEvents } from "../desktop/commands/EventBus";
import { getDesktopEnv } from "../desktop/desktopEnv";
import { useFocusScope, useRegisterShortcuts } from "../desktop/hooks";

const REQUIRED_SALE_COLUMNS = [
  "productGroup",
  "grWt",
  "netWt",
  "itRateOn",
  "itRate",
  "itAmt",
  "totalAmt",
];

const isRequiredItemValueMissing = (value) =>
  value == null || String(value).trim() === "";

const saleRowHasEntry = (row) =>
  (row?.data || []).some((cell) => {
    if (cell.id === "no") return false;
    if (cell.id === "image" && cell.imageFile) return true;
    if (cell.id === "itRateOn" || cell.id === "lType") {
      const value = String(cell.value || "").trim();
      return value !== "" && value !== "NWt";
    }
    return !isRequiredItemValueMissing(cell.value);
  });

const collectSaleItemFieldErrors = (rows) => {
  const started = rows.filter(saleRowHasEntry);
  const targets = started.length ? started : rows.slice(0, 1);
  const errors = {};
  targets.forEach((row) => {
    const valueOf = (fieldId) =>
      row.data.find((item) => item.id === fieldId)?.value;
    const missing = REQUIRED_SALE_COLUMNS.filter((fieldId) =>
      isRequiredItemValueMissing(valueOf(fieldId)),
    );
    const productGroupSelected = !isRequiredItemValueMissing(
      valueOf("productGroup"),
    );
    const tagNoMissing = isRequiredItemValueMissing(valueOf("tagNo"));
    if (
      productGroupSelected &&
      tagNoMissing &&
      isRequiredItemValueMissing(valueOf("pcs"))
    ) {
      missing.push("pcs");
    }
    if (missing.length) errors[row.id] = missing;
  });
  return errors;
};

const isZeroOrNullValue = (value) =>
  value === 0 ||
  value === "0" ||
  value === null ||
  value === undefined ||
  value === "";

/** Skip empty / zero weights so `current_gwt: 0` does not hide `gwt`. */
const firstPositiveWeight = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const num = Number(value);
    if (!Number.isNaN(num) && num !== 0) return value;
  }
  return "";
};

const pickProductGrossWeight = (product = {}) => {
  const fromApi = firstPositiveWeight(
    product.current_gwt,
    product.gwt,
    product.GWT,
    product.current_GWT,
    product.gross_weight,
    product.grossWeight,
  );
  if (fromApi !== "") return fromApi;

  const nwt = Number(product.NWT ?? product.nwt ?? 0);
  const lwt = Number(
    product.LWt_customer ??
    product.lwt_customer ??
    product.LWT ??
    product.lwt ??
    0,
  );
  if (nwt > 0) {
    const computed = nwt + (Number.isNaN(lwt) ? 0 : lwt);
    return Number.isNaN(computed) ? "" : computed;
  }
  return "";
};

/**
 * Product "show" API may return `data` as the product, or `data.product` /
 * `data.products` / `data.items` (tag prefix search).
 * Map snake_case fields to names used by Sales row mapping.
 */
function extractProductFromShowResponse(response, tagNo) {
  const d = response?.data ?? {};
  let raw = d.product ?? response?.data?.data?.product ?? null;
  if (!raw && Array.isArray(d.items)) {
    const tn = String(tagNo || "")
      .trim()
      .toLowerCase();
    raw =
      d.items.find((p) => p && String(p.tag_no || "").toLowerCase() === tn) ??
      (d.items.length === 1 ? d.items[0] : null);
  }
  if (!raw && Array.isArray(d.products)) {
    const tn = String(tagNo || "")
      .trim()
      .toLowerCase();
    raw =
      d.products.find(
        (p) => p && String(p.tag_no || "").toLowerCase() === tn,
      ) ?? d.products[0];
  }
  // Single product returned directly on data (no wrapper)
  if (
    !raw &&
    d &&
    typeof d === "object" &&
    !Array.isArray(d) &&
    (d.id !== undefined ||
      d.product_id !== undefined ||
      (d.tag_no !== undefined && d.tag_no !== null && d.tag_no !== ""))
  ) {
    raw = d;
  }
  if (!raw || typeof raw !== "object") return null;
  return {
    ...raw,
    product_group: raw.product_group ?? raw.product_group_id,
    current_gwt: pickProductGrossWeight(raw),
    LWt_customer: raw.LWt_customer ?? raw.lwt_customer,
    NWT: raw.NWT ?? raw.nwt,
    LBR_type: raw.LBR_type ?? raw.lbr_type,
    LBR: raw.LBR ?? raw.lbr_percent,
    LBR_rate: raw.LBR_rate ?? raw.lbr_rate,
    LBR_amt: raw.LBR_amt ?? raw.lbr_amt,
    othamt: raw.othamt ?? raw.oth_amt,
    MRP: raw.MRP ?? raw.mrp,
    current_pcs: raw.current_pcs ?? raw.pcs,
  };
}

const normalizeCustomerUser = (user = {}) => ({
  id: user.id,
  fullName: user.name || "",
  name: user.name || "",
  mobile: user.mobile || "",
  email: user.email || "",
  city: user.city || "",
  address: user.address || "",
  state: user.state || "",
  area: user.area || "",
  aadharNo: user.aadhar_no || "",
  panNo: user.pan_no || "",
  birthDate: user.birth_date || "",
  maritalStatus: user.marital_status || "",
  anniversaryDate: user.anniversary_date || "",
  saleBalance: user.sale_balance || "",
  balance: user.users_balance ?? user.balance ?? "",
  companyName: user.company_name || "",
  companyAddress: user.company_address || "",
  gstNumber: user.GST_number || "",
  reference: user.reference || "",
  status: user.status || "",
});

const mergeCustomerUsers = (existingUsers = [], incomingUsers = []) => {
  const merged = [...existingUsers];

  incomingUsers.forEach((incomingUser) => {
    const existingIndex = merged.findIndex(
      (user) =>
        String(user.id) === String(incomingUser.id) ||
        (user.mobile &&
          incomingUser.mobile &&
          user.mobile === incomingUser.mobile),
    );

    if (existingIndex >= 0) {
      merged[existingIndex] = { ...merged[existingIndex], ...incomingUser };
    } else {
      merged.push(incomingUser);
    }
  });

  return merged.sort((b, a) => a.id - b.id);
};

const FALLBACK_CITIES_BY_STATE = {
  MH: ["Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Kolhapur"],
  GJ: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar"],
  RJ: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"],
  DL: ["New Delhi", "Dwarka", "Rohini", "Saket", "Karol Bagh", "Lajpat Nagar"],
  KA: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Belagavi", "Davangere"],
  TN: [
    "Chennai",
    "Coimbatore",
    "Madurai",
    "Salem",
    "Tiruchirappalli",
    "Vellore",
  ],
  TG: [
    "Hyderabad",
    "Warangal",
    "Nizamabad",
    "Khammam",
    "Karimnagar",
    "Sangareddy",
  ],
  WB: ["Kolkata", "Howrah", "Durgapur", "Siliguri", "Asansol", "Kharagpur"],
  UP: ["Lucknow", "Kanpur", "Varanasi", "Agra", "Noida", "Ghaziabad"],
  MP: ["Indore", "Bhopal", "Gwalior", "Jabalpur", "Ujjain", "Sagar"],
};

const Sales = ({ masterGoldRate, masterSilverRate, showLiveRate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const desktopEnv = getDesktopEnv();
  const queueDetailsId = resolveQueueDetailsId(location.state);
  const customOrderId = location.state?.customOrderId;
  const customerUserId = location.state?.customerUserId;
  const repairingOrderId = location.state?.repairingOrderId;
  const isFromRepairingOrder = location.state?.isFromRepairingOrder;
  const isQueueLocked = !!(queueDetailsId || customOrderId);
  const { buttonClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    return rate;
  };

  /**
   * Gold: fine ₹/g × purity factor.
   * - Prefer karat parsed from product group **name** (e.g. "18 Carat…") when present — fixes metal_group purity stored as % (e.g. 72) wrongly used as karat (72/24=3).
   * - Else if purity > 24 and ≤ 100: treat as **% fine** (75 → 0.75).
   * - Else: treat as **karat** (18 → 18/24).
   */
  const calculatePurityBasedRate = (
    purity,
    rate,
    metalType,
    productLabel = "",
  ) => {
    const purityValue = parseFloat(
      typeof purity === "string" ? purity.trim() : purity,
    );
    if (isNaN(purityValue)) return rate;

    if (metalType === "Gold") {
      const label = String(productLabel || "").toLowerCase();
      const karatFromLabel = (() => {
        if (/\b24\s*(carat|ct)\b|\b24\s*k\b|\b24k\b/.test(label)) return 24;
        if (/\b22\s*(carat|ct)\b|\b22\s*k\b|\b22k\b/.test(label)) return 22;
        if (/\b18\s*(carat|ct)\b|\b18\s*k\b|\b18k\b/.test(label)) return 18;
        if (/\b14\s*(carat|ct)\b|\b14\s*k\b|\b14k\b/.test(label)) return 14;
        return null;
      })();
      if (karatFromLabel != null) {
        return rate * (karatFromLabel / 24);
      }
      if (purityValue > 24 && purityValue <= 100) {
        return rate * (purityValue / 100);
      }
      return rate * (purityValue / 24);
    }
    if (metalType === "Silver") {
      return (rate * purityValue) / 999;
    }

    return rate;
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "tagNo",
      label: "Tag No",
      width: "100px",
      value: (row, onChange) => {
        const filtered = getFilteredItems(row.tagNo || "", "tagNo");
        const visibleItems = Math.min(filtered.length, 5);
        const dropdownHeight = visibleItems * 35;
        return (
          <div className="position-relative">
            <input
              type="text"
              value={row.tagNo}
              onChange={(e) => {
                const newTagNo = e.target.value;
                onChange(row.id, "tagNo", newTagNo);
              }}
              onFocus={(e) => {
                if (!isQueueLocked) {
                  const rowIndex = tableData.findIndex((r) => r.id === row.id);
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "tagNo",
                  );
                  handleFocus(rowIndex, colIndex);
                }
              }}
              disabled={isQueueLocked}
              className={`form-control ${isQueueLocked ? "bg-light" : ""
                } ${buttonClass}`}
              placeholder={
                isQueueLocked ? "From order queue" : "Enter tag number"
              }
            />
            {!isQueueLocked &&
              activeDropdown.row ===
              tableData.findIndex((r) => r.id === row.id) &&
              activeDropdown.col ===
              tableData[
                tableData.findIndex((r) => r.id === row.id)
              ].data.findIndex((cell) => cell.id === "tagNo") &&
              activeDropdown.type === "tagNo" &&
              (filtered.length > 0 || tagNoApiCalled) && (
                <div
                  className="dropdown-menu show"
                  style={{
                    minWidth: "100px",
                    width: "max-content",
                    maxWidth: "min(90vw, 420px)",
                    maxHeight: `${Math.max(dropdownHeight, filtered.length === 0 ? 35 : 0)}px`,
                    height: `${Math.max(dropdownHeight, filtered.length === 0 ? 35 : 0)}px`,
                    overflowY: filtered.length > 5 ? "auto" : "hidden",
                    overflowX: "auto",
                    padding: 0,
                  }}
                >
                  {filtered.length > 0 ? (
                    filtered.map((item, index) => (
                      <div
                        key={item.value}
                        className={`dropdown-item ${index === highlightedIndex ? "active" : ""
                          }`}
                        onClick={() => {
                          tagNoSearchRequestIdRef.current += 1;
                          const selectedRowIndex = tableData.findIndex(
                            (r) => r.id === row.id,
                          );
                          onChange(row.id, "tagNo", item.label);
                          setActiveDropdown({
                            row: null,
                            col: null,
                            type: null,
                          });
                          setDropdownData((prev) => ({
                            ...prev,
                            tagNo: [],
                          }));
                          setTagNoApiCalled(false);
                          setLastSearchedTagPrefix(String(item.label || ""));
                          sendTagNoToApi(item.label, selectedRowIndex);
                        }}
                        ref={(el) => (dropdownItemsRef.current[index] = el)}
                        title={item.label}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "visible",
                          textOverflow: "clip",
                        }}
                      >
                        {item.label}
                      </div>
                    ))
                  ) : tagNoApiCalled ? (
                    <div
                      className="dropdown-item text-muted"
                      style={{ cursor: "default" }}
                    >
                      No matching items
                    </div>
                  ) : null}
                </div>
              )}
          </div>
        );
      },
    },

    {
      id: "productGroup",
      label: (
        <>
          Product Group 
        </>
      ),
      width: "120px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "design",
      label: "Design",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.design}
          onChange={(e) => onChange(row.id, "design", e.target.value)}
        />
      ),
    },
    {
      id: "size",
      label: "Size",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.size}
          onChange={(e) => onChange(row.id, "size", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: (
        <>
          Gr. Wt 
        </>
      ),
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "Ls.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: (
        <>
          Net. Wt 
        </>
      ),
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "itRateOn",
      label: (
        <>
          RateOn 
        </>
      ),
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.itRateOn}
          onChange={(e) => onChange(row.id, "itRateOn", e.target.value)}
        />
      ),
    },
    {
      id: "itRate",
      label: (
        <>
          Rate 
        </>
      ),
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itRate}
          onChange={(e) => onChange(row.id, "itRate", e.target.value)}
        />
      ),
    },
    {
      id: "itAmt",
      label: (
        <>
          Amount 
        </>
      ),
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itAmt}
          onChange={(e) => onChange(row.id, "itAmt", e.target.value)}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr On",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "L.Rate",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "L.Amount",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "Other",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "huid",
      label: "HUID",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.huid}
          onChange={(e) => onChange(row.id, "huid", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {row.image && (
            <img
              src={`${node_imageBaseUrl}/img/product/${row.image}`}
              alt="Product"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            />
          )}
          <input
            type="text"
            value={row.image}
            onChange={(e) => onChange(row.id, "image", e.target.value)}
            style={{ fontSize: "12px" }}
          />
        </div>
      ),
    },
    {
      id: "totalAmt",
      label: (
        <>
          TotalAmt 
        </>
      ),
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
  ];

  // Define payment columns structure
  const saleDraftKeyRef = useRef(null);
  /** Always points at latest openUrd implementation (avoids stale customer fields). */
  const openUrdFromPaymentRowRef = useRef((_row) => { });
  const openUrdFromPaymentRow = useCallback((row) => {
    openUrdFromPaymentRowRef.current(row);
  }, []);

  // Fetch customers only when they are actually needed.
  const fetchUserss = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";

    try {
      const response = await listCustomerUsers({ search: searchText });
      const usersData = Array.isArray(response?.data?.customers)
        ? response.data.customers
        : Array.isArray(response?.data?.items)
          ? response.data.items
          : Array.isArray(response?.data)
            ? response.data
            : [];

      if (isErpApiSuccess(response) && Array.isArray(usersData)) {
        const fetchedUserss = usersData.map((user) =>
          normalizeCustomerUser(user),
        );

        setUserss((prevUsers) =>
          searchText
            ? mergeCustomerUsers(prevUsers, fetchedUserss)
            : mergeCustomerUsers([], fetchedUserss),
        );

        if (searchText) {
          customerSearchCacheRef.current.add(searchText.toLowerCase());
        }

        return fetchedUserss;
      }

      console.error("Unexpected data format:", response);
    } catch (error) {
      console.error("Error fetching User Details:", error);
    }

    return [];
  };

  const fetchCustomerById = async (customerId) => {
    if (customerId === undefined || customerId === null || customerId === "") {
      return null;
    }

    try {
      const response = await showCustomerUser({ id: customerId });
      const rawCustomer =
        response?.data?.customer ??
        response?.data?.user ??
        response?.data?.item ??
        (Array.isArray(response?.data?.customers)
          ? response.data.customers[0]
          : null) ??
        (Array.isArray(response?.data?.items)
          ? response.data.items[0]
          : null) ??
        (Array.isArray(response?.data?.data) ? response.data.data[0] : null) ??
        (Array.isArray(response?.data) ? response.data[0] : null);

      if (isErpApiSuccess(response) && rawCustomer) {
        const normalizedCustomer = normalizeCustomerUser(rawCustomer);
        setUserss((prevUsers) =>
          mergeCustomerUsers(prevUsers, [normalizedCustomer]),
        );
        return normalizedCustomer;
      }

      console.error("Unexpected customer response format:", response);
    } catch (error) {
      console.error("Error fetching customer by ID:", error);
    }

    return null;
  };

  // Listen for API URL changes and clear cached lookup data.
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in Sales, clearing cached lookup data...",
        event.detail,
      );
      setUserss([]);
      customerSearchCacheRef.current.clear();
      productGroupOptionsRef.current = [];
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setLastSearchedProductGroupPrefix("");
      setProductGroupApiCalled(false);
      setProductGroupPoolHeader(null);
      setProductGroupSearchLoading(false);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const ensureProductGroupsLoaded = async () => productGroupOptionsRef.current;

  const resolveProductGroupOption = async (pgId) => {
    if (pgId == null || pgId === "") return null;
    const cached = productGroupOptionsRef.current.find(
      (pg) => String(pg.value) === String(pgId),
    );
    if (cached) return cached;

    const fetched = await fetchProductGroupOptionById(pgId);
    if (fetched) {
      productGroupOptionsRef.current = mergeProductGroupOptions(
        productGroupOptionsRef.current,
        [fetched],
      );
    }
    return fetched;
  };

  const applyPoolHeaderFromGroup = (group) => {
    if (!group) {
      setProductGroupPoolHeader(null);
      return;
    }
    setProductGroupPoolHeader({
      pcs: numPool(group.pool_pcs),
      nwt: numPool(group.pool_nwt),
    });
  };

  const getProductGroupOptions = () =>
    mergeProductGroupOptions(
      productGroupOptionsRef.current,
      dropdownData.productGroup,
    );

  /** Match row product group text against loaded options (label, name, id, short_code). */
  const findProductGroupMatch = (cellValue, optionsList) => {
    const raw = String(cellValue ?? "").trim();
    if (!raw) return null;
    const groups = optionsList || getProductGroupOptions();
    const exact = groups.find(
      (g) =>
        g.label === cellValue ||
        g.name === cellValue ||
        g.short_code === cellValue ||
        String(g.value) === raw,
    );
    if (exact) return exact;

    const nl = raw.toLowerCase();
    const stripPoolSuffix = (s) =>
      String(s)
        .replace(/\s*—\s*pool:.+$/i, "")
        .trim()
        .toLowerCase();

    const caseInsensitive = groups.find((g) => {
      const label = String(g.label ?? "").trim();
      const name = String(g.name ?? "").trim();
      const code = String(g.short_code ?? "").trim();
      return (
        label.toLowerCase() === nl ||
        name.toLowerCase() === nl ||
        code.toLowerCase() === nl ||
        stripPoolSuffix(label) === nl ||
        stripPoolSuffix(name) === nl
      );
    });
    if (caseInsensitive) return caseInsensitive;

    // Partial / truncated cell text (narrow column) — prefer unique prefix match
    const prefixMatches = groups.filter((g) => {
      const label = stripPoolSuffix(g.label ?? "");
      const name = stripPoolSuffix(g.name ?? "");
      if (!label && !name) return false;
      return (
        (label && (label.startsWith(nl) || nl.startsWith(label))) ||
        (name && (name.startsWith(nl) || nl.startsWith(name)))
      );
    });
    if (prefixMatches.length === 1) return prefixMatches[0];

    return null;
  };

  const resolveProductGroupForSubmit = async (rowData, rowMeta) => {
    let match = findProductGroupMatch(rowData.productGroup);
    if (match) return match;

    const pgCell = rowMeta?.data?.find((c) => c.id === "productGroup");
    const pgIdFromCell =
      pgCell?.productGroupId != null &&
        !Number.isNaN(Number(pgCell.productGroupId))
        ? Number(pgCell.productGroupId)
        : null;

    const pgId =
      rowMeta?.erpProductGroupId ??
      pgIdFromCell ??
      (Number.isFinite(Number(rowData.productGroup)) &&
        String(rowData.productGroup).trim() !== ""
        ? rowData.productGroup
        : null);

    if (pgId != null && pgId !== "") {
      match = await resolveProductGroupOption(pgId);
      if (match) {
        return findProductGroupMatch(rowData.productGroup) || match;
      }
    }

    const tagNo = (rowData.tagNo && rowData.tagNo.toString().trim()) || "";
    if (tagNo) {
      try {
        const tagRes = await showProducts({ tag_no: tagNo });
        const p = extractProductFromShowResponse(tagRes, tagNo);
        if (isErpApiSuccess(tagRes) && p?.product_group != null) {
          const resolved = await resolveProductGroupOption(p.product_group);
          if (resolved) {
            return findProductGroupMatch(rowData.productGroup) || resolved;
          }
        }
      } catch (tagErr) {
        console.warn("Could not resolve product group for tag", tagNo, tagErr);
      }
    }

    // Untagged rows: search API by typed product group name (cache may be empty)
    const pgText = String(rowData.productGroup ?? "").trim();
    if (pgText.length >= 3) {
      try {
        const opts = await searchProductGroupSelectOptions(pgText);
        if (opts?.length) {
          productGroupOptionsRef.current = mergeProductGroupOptions(
            productGroupOptionsRef.current,
            opts,
          );
          match = findProductGroupMatch(pgText, opts);
          if (match) return match;
        }
      } catch (searchErr) {
        console.warn(
          "Could not resolve product group via search",
          pgText,
          searchErr,
        );
      }
    }

    return null;
  };

  // Search product groups via API when user types 3+ characters
  const fetchProductGroupByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      return dropdownData.productGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      productGroupOptionsRef.current = mergeProductGroupOptions(
        productGroupOptionsRef.current,
        opts,
      );
      setDropdownData((prev) => ({
        ...prev,
        productGroup: opts,
      }));
      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
      if (opts.length > 0) {
        applyPoolHeaderFromGroup(opts[0]);
      }
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  // Function to fetch tag_no options by prefix (when user types 3+ digits)
  const fetchTagNoByPrefix = async (prefix) => {
    if (!prefix || prefix.length < 3) {
      return;
    }

    // Avoid duplicate API calls for the same prefix
    if (lastSearchedTagPrefix === prefix) {
      return;
    }

    const requestId = ++tagNoSearchRequestIdRef.current;

    try {
      const response = await showProducts({ tag_no: prefix });
      // Selection / clear happened while this request was in flight — ignore
      if (requestId !== tagNoSearchRequestIdRef.current) {
        return;
      }

      const d = response?.data ?? response;

      if (isErpApiSuccess(response) && d) {
        let tagNos = [];

        if (Array.isArray(d.tag_nos)) {
          tagNos = d.tag_nos.map((tag) => ({
            label: tag,
            value: tag,
          }));
        } else if (d.product) {
          tagNos = [
            {
              label: d.product.tag_no,
              value: d.product.tag_no,
            },
          ];
        } else if (Array.isArray(d.products)) {
          tagNos = d.products.map((p) => ({
            label: p.tag_no,
            value: p.tag_no,
          }));
        } else if (Array.isArray(d.items)) {
          tagNos = d.items
            .filter(
              (p) => p && p.tag_no != null && String(p.tag_no).trim() !== "",
            )
            .map((p) => ({
              label: String(p.tag_no),
              value: String(p.tag_no),
            }));
        }

        // Remove duplicates
        const uniqueTagNos = Array.from(
          new Map(tagNos.map((item) => [item.value, item])).values(),
        );

        // Update dropdown data with fetched tag_no options
        setDropdownData((prev) => ({
          ...prev,
          tagNo: uniqueTagNos,
        }));

        setLastSearchedTagPrefix(prefix);
        setTagNoApiCalled(true); // Mark that API was called

        // Only show dropdown if there are results
        if (uniqueTagNos.length > 0) {
          // Find the row that triggered this search to show dropdown
          const currentRow = tableData.find((row) => {
            const tagNoCell = row.data.find((cell) => cell.id === "tagNo");
            return tagNoCell && tagNoCell.value === prefix;
          });

          if (currentRow) {
            const rowIndex = tableData.findIndex((r) => r.id === currentRow.id);
            const colIndex = currentRow.data.findIndex(
              (cell) => cell.id === "tagNo",
            );

            if (rowIndex !== -1 && colIndex !== -1) {
              handleDropdownClick(rowIndex, colIndex, "tagNo");
            }
          }
        } else {
          // No results - show dropdown with "No matching items" message
          const currentRow = tableData.find((row) => {
            const tagNoCell = row.data.find((cell) => cell.id === "tagNo");
            return tagNoCell && tagNoCell.value === prefix;
          });

          if (currentRow) {
            const rowIndex = tableData.findIndex((r) => r.id === currentRow.id);
            const colIndex = currentRow.data.findIndex(
              (cell) => cell.id === "tagNo",
            );

            if (rowIndex !== -1 && colIndex !== -1) {
              handleDropdownClick(rowIndex, colIndex, "tagNo");
            }
          }
        }
      } else {
        if (requestId !== tagNoSearchRequestIdRef.current) {
          return;
        }
        // If no products found or error message, clear dropdown
        setDropdownData((prev) => ({
          ...prev,
          tagNo: [],
        }));
        setTagNoApiCalled(true); // Mark that API was called (even if no results)

        // Show dropdown with "No matching items" message
        const currentRow = tableData.find((row) => {
          const tagNoCell = row.data.find((cell) => cell.id === "tagNo");
          return tagNoCell && tagNoCell.value === prefix;
        });

        if (currentRow) {
          const rowIndex = tableData.findIndex((r) => r.id === currentRow.id);
          const colIndex = currentRow.data.findIndex(
            (cell) => cell.id === "tagNo",
          );

          if (rowIndex !== -1 && colIndex !== -1) {
            handleDropdownClick(rowIndex, colIndex, "tagNo");
          }
        }
      }
    } catch (error) {
      if (requestId !== tagNoSearchRequestIdRef.current) {
        return;
      }
      console.error("Error fetching tag_no options by prefix:", error);
      // On error, clear the dropdown
      setDropdownData((prev) => ({
        ...prev,
        tagNo: [],
      }));
      setTagNoApiCalled(true); // Mark that API was called (even on error)

      // Show dropdown with "No matching items" message on error
      const currentRow = tableData.find((row) => {
        const tagNoCell = row.data.find((cell) => cell.id === "tagNo");
        return tagNoCell && tagNoCell.value === prefix;
      });

      if (currentRow) {
        const rowIndex = tableData.findIndex((r) => r.id === currentRow.id);
        const colIndex = currentRow.data.findIndex(
          (cell) => cell.id === "tagNo",
        );

        if (rowIndex !== -1 && colIndex !== -1) {
          handleDropdownClick(rowIndex, colIndex, "tagNo");
        }
      }
    }
  };

  // Function to send tag number to API and autofill the target row
  const sendTagNoToApi = async (tagNo, targetRowIndex = null) => {
    try {
      const response = await showProducts({ tag_no: tagNo });
      const product = extractProductFromShowResponse(response, tagNo);

      if (isErpApiSuccess(response) && product) {
        const pgObj = await resolveProductGroupOption(product.product_group);
        const fieldMapping = {
          product_group: "productGroup",
          design: "design",
          size: "size",
          current_pcs: "pcs",
          current_gwt: "grWt",
          LWt_customer: "lsWt",
          NWT: "netWt",
          LBR_type: "lType",
          LBR: "lbrPercentage",
          LBR_rate: "lbrRate",
          LBR_amt: "lbrAmt",
          othamt: "othAmt",
          MRP: "mrp",
          image: "image",
          huid: "huid",
        };

        setTableData((prevData) => {
          let rowIndexes = [];
          if (
            targetRowIndex != null &&
            targetRowIndex >= 0 &&
            targetRowIndex < prevData.length
          ) {
            rowIndexes = [targetRowIndex];
          } else {
            rowIndexes = prevData
              .map((row, index) => ({ row, index }))
              .filter(({ row }) => {
                const tagNoCell = row.data.find((cell) => cell.id === "tagNo");
                return (
                  tagNoCell &&
                  String(tagNoCell.value || "").trim() === String(tagNo).trim()
                );
              })
              .map(({ index }) => index);
          }

          if (rowIndexes.length === 0) {
            console.warn("No matching rows found for tagNo:", tagNo);
            return prevData;
          }

          const newData = prevData.map((row) => ({
            ...row,
            data: row.data.map((cell) => ({ ...cell })),
          }));

          rowIndexes.forEach((rowIndex) => {
            // Keep tag no in sync (selection may have raced ahead of state)
            const tagCell = newData[rowIndex].data.find(
              (cell) => cell.id === "tagNo",
            );
            if (tagCell) tagCell.value = tagNo;

            if (product?.id != null) {
              newData[rowIndex].erpProductId = product.id;
            }
            if (
              product?.product_group != null &&
              product.product_group !== ""
            ) {
              newData[rowIndex].erpProductGroupId = product.product_group;
            }
            // Update each field based on the mapping
            Object.entries(fieldMapping).forEach(([apiField, tableField]) => {
              const cellIndex = newData[rowIndex].data.findIndex(
                (cell) => cell.id === tableField,
              );
              if (cellIndex !== -1) {
                if (apiField === "product_group") {
                  // Convert product group ID to label
                  const pgId = product[apiField];
                  const pgName = pgObj ? pgObj.name || pgObj.label : pgId; // fallback to ID
                  newData[rowIndex].data[cellIndex].value = pgName;
                  if (pgId != null && pgId !== "") {
                    newData[rowIndex].data[cellIndex].productGroupId = pgId;
                  }
                } else if (apiField === "image" && product[apiField]) {
                  // Use node_imageBaseUrl for the image field
                  newData[rowIndex].data[cellIndex].value = product[apiField];
                  newData[rowIndex].data[cellIndex].imagePreview =
                    `${node_imageBaseUrl}/${product[apiField]}`;
                } else if (apiField === "LWt_customer") {
                  // Use LWt_customer with fallback to LWT for backward compatibility
                  newData[rowIndex].data[cellIndex].value =
                    product.LWt_customer || product.LWT || product.lwt || "";
                } else if (apiField === "huid") {
                  newData[rowIndex].data[cellIndex].value =
                    product.huid || product.HUID || "";
                } else if (apiField === "LBR_type") {
                  newData[rowIndex].data[cellIndex].value =
                    product.LBR_type ??
                    product.lbr_type ??
                    product.lbr_type_name ??
                    "NWt";
                } else if (apiField === "LBR") {
                  newData[rowIndex].data[cellIndex].value =
                    product.LBR ?? product.lbr_percent ?? product.lbr ?? "";
                } else if (apiField === "LBR_rate") {
                  newData[rowIndex].data[cellIndex].value =
                    product.LBR_rate ?? product.lbr_rate ?? "";
                } else if (apiField === "LBR_amt") {
                  newData[rowIndex].data[cellIndex].value =
                    product.LBR_amt ?? product.lbr_amt ?? "";
                } else if (apiField === "NWT") {
                  newData[rowIndex].data[cellIndex].value =
                    product.NWT ?? product.nwt ?? "";
                } else if (apiField === "current_gwt") {
                  newData[rowIndex].data[cellIndex].value =
                    pickProductGrossWeight(product);
                } else {
                  newData[rowIndex].data[cellIndex].value =
                    product[apiField] || "";
                }

                // Store current_pcs value for validation
                if (apiField === "current_pcs") {
                  const pcsCell = newData[rowIndex].data.find(
                    (cell) => cell.id === "pcs",
                  );
                  if (pcsCell) {
                    pcsCell.current_pcs = product[apiField] || "0";
                  }
                }
              }
            });

            // After tag fields + weights are filled: recompute metal rate/amount,
            // then derive L.Rate / L.Amount from Lbr % (product may omit LBR_rate).
            calculateAndUpdateItRate(newData, rowIndex);
            const lbrPctVal = parseFloat(
              newData[rowIndex].data.find((c) => c.id === "lbrPercentage")
                ?.value || 0,
            );
            const lbrRateVal = parseFloat(
              newData[rowIndex].data.find((c) => c.id === "lbrRate")?.value ||
              0,
            );
            if (lbrPctVal > 0) {
              newData[rowIndex].lastChangedLaborField = "lbrPercentage";
            } else if (lbrRateVal > 0) {
              newData[rowIndex].lastChangedLaborField = "lbrRate";
            }
            handleLaborCalculations(newData, rowIndex);

            // Handle charges array for othAmt
            const othAmtIndex = newData[rowIndex].data.findIndex(
              (cell) => cell.id === "othAmt",
            );
            if (othAmtIndex !== -1) {
              const mappedCharges = mapApiChargesToOtherAmountEntries(
                product.charges,
              );

              // If charges exist but othamt is missing/zero, compute sum from charges
              const chargesTotal = mappedCharges.reduce(
                (sum, c) => sum + parseFloat(c.amount || 0),
                0,
              );
              const othamtValue = product.othamt;
              const resolvedOthAmt =
                othamtValue && parseFloat(othamtValue) > 0
                  ? parseFloat(othamtValue).toFixed(2)
                  : chargesTotal.toFixed(2);

              newData[rowIndex].data[othAmtIndex].value = resolvedOthAmt;
              newData[rowIndex].data[othAmtIndex].otherAmountEntries =
                mappedCharges;
            }

            // Calculate total amount
            const itAmtIndex = newData[rowIndex].data.findIndex(
              (cell) => cell.id === "itAmt",
            );
            const lbrAmtIndex = newData[rowIndex].data.findIndex(
              (cell) => cell.id === "lbrAmt",
            );
            const totalAmtIndex = newData[rowIndex].data.findIndex(
              (cell) => cell.id === "totalAmt",
            );

            const mrpIndex = newData[rowIndex].data.findIndex(
              (cell) => cell.id === "mrp",
            );
            const itAmt = parseFloat(
              newData[rowIndex].data[itAmtIndex]?.value || 0,
            );
            const lbrAmt = parseFloat(
              newData[rowIndex].data[lbrAmtIndex]?.value || 0,
            );
            const othAmt = parseFloat(
              newData[rowIndex].data[othAmtIndex]?.value || 0,
            );
            const mrp = parseFloat(
              newData[rowIndex].data[mrpIndex]?.value || 0,
            );

            let totalAmt = 0;
            if (mrp > 0) {
              totalAmt = mrp;
            } else {
              totalAmt = itAmt + lbrAmt + othAmt;
            }

            if (totalAmtIndex !== -1) {
              const totalAmtCell = newData[rowIndex].data[totalAmtIndex];
              if (!totalAmtCell?.totalAmountDetails?.isManual) {
                totalAmtCell.value = roundLineTotalAmount(totalAmt);
                if (totalAmtCell.totalAmountDetails) {
                  delete totalAmtCell.totalAmountDetails;
                }
              }
            }
          });

          return newData;
        });
      } else {
        console.error("API returned non-success status:", response);
      }
    } catch (error) {
      console.error("Error in API call:", error);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const response = await listBankAccounts();
      const rows = Array.isArray(response?.data?.bank_accounts)
        ? response.data.bank_accounts
        : [];

      if (response?.success && Array.isArray(rows)) {
        const fetchedBankDetails = rows.map((bank) => {
          // console.log("Bank record from API:", bank);

          return {
            id: bank.id,
            bankName: bank.bank_name || "",
            accountHolder: bank.holder_name || "",
            accountType: bank.account_type || "",
            ifscCode: bank.ifsc_code || bank.IFSC_code || "",
            accountNumber: bank.account_number || "",
            openingBalance: bank.opening_balance || "",
            balance: bank.opening_balance ? `₹${bank.opening_balance}` : "₹0",
            status: bank.status === 1 ? "Active" : "Inactive",
            statusClass:
              bank.status === 1
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
            useForInvoice: bank.use_for_invoice === 1,
            useForPOS: bank.use_for_pos === 1,
          };
        });

        // Sort by ID in descending order (newest first)
        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        // Also populate paymentTypes for the dropdown
        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);

        // console.log("Bank accounts set successfully:", fetchedBankDetails);
        // console.log("Payment types set successfully:", paymentOptions);
      } else if (
        response.data &&
        response.data.message === "radha" &&
        (!response.data.data || response.data.data.length === 0)
      ) {
        // Handle empty data case
        // console.log("No bank accounts found");
        setBankAccounts([]);
        setPaymentTypes([]);
        toast("No bank accounts found");
      } else if (response.data && response.data.success === false) {
        // Handle error case
        console.error("Server returned error:", response.data.message);
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.data.message || "Failed to fetch bank details");
      } else {
        // Handle unexpected format
        console.error("Unexpected data format:", response.data);
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details",
      );
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    lType: ["NWt", "GWt", "Pcs", "Fixed"], // Keeping this as it is since it's not from API
    itRateOn: ["NWt", "GWt"], // Adding new options for ItRateOn
    tagNo: [],
  });

  // Columns that should have dropdown functionality
  const dropdownColumns = [
    "productGroup",
    "lType",
    "rateOn",
    "itRateOn",
    "tagNo",
  ];

  const getDefaultCellValue = (colId) => {
    if (colId === "itRateOn" || colId === "lType") return "NWt";
    return "";
  };

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: getDefaultCellValue(col.id),
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) => {
      const rowIds = Object.keys(prev);
      if (!rowIds.length) return prev;

      const fresh = collectSaleItemFieldErrors(tableData);
      let changed = false;
      const next = {};
      rowIds.forEach((rowId) => {
        const stillMissing = prev[rowId].filter((fieldId) =>
          fresh[rowId]?.includes(fieldId),
        );
        if (stillMissing.length !== prev[rowId].length) changed = true;
        if (stillMissing.length) next[rowId] = stillMissing;
      });
      return changed ? next : prev;
    });
  }, [tableData]);
  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedTagPrefix, setLastSearchedTagPrefix] = useState("");
  const [tagNoApiCalled, setTagNoApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [productGroupPoolHeader, setProductGroupPoolHeader] = useState(null);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const dropdownRef = useRef(null);
  const tableContainerRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({}); // Add refs for tooltips
  const productGroupOptionsRef = useRef([]);
  const customerSearchCacheRef = useRef(new Set());
  // Bumps on tag select / clear so stale prefix fetches cannot reopen the menu
  const tagNoSearchRequestIdRef = useRef(0);

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [hoveredNoRowIndex, setHoveredNoRowIndex] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const totalAmountInputRef = useRef(null);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    zoomLevel: 1,
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  const [totalAmountModal, setTotalAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    amount: "",
    gst: "3",
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  const [totalAmountEnterPress, setTotalAmountEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  useEffect(() => {
    if (totalAmountModal.isOpen) {
      const timer = setTimeout(() => {
        if (totalAmountInputRef.current) {
          totalAmountInputRef.current.focus();
          totalAmountInputRef.current.select();
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [totalAmountModal.isOpen]);

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const [billDate, setBillDate] = useState("");
  const [createdBy, setCreatedBy] = useState(null);

  const sessionBranchId = useMemo(() => {
    const u = getErpAuthState()?.user || {};
    const n = Number(u.branch_id ?? u.branchId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, []);
  const { selectOptions: employeeOptions, loading: employeesLoading } =
    useHrmsEmployees(sessionBranchId);

  const resolveCurrentUserId = () => {
    const legacyUser =
      (typeof getUserData === "function" ? getUserData() : null) || {};
    const erpUser = getErpAuthState()?.user || {};
    const id = Number(
      legacyUser?.id ??
      legacyUser?.ua_id ??
      erpUser?.id ??
      erpUser?.ua_id ??
      erpUser?.user_id,
    );
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  // Default Created By to the logged-in employee once the list loads
  useEffect(() => {
    if (createdBy || employeeOptions.length === 0) return;
    const currentId = resolveCurrentUserId();
    if (!currentId) return;
    const match = employeeOptions.find(
      (opt) => Number(opt.value) === currentId,
    );
    if (match) setCreatedBy(match);
  }, [employeeOptions, createdBy]);

  const [tagOptions, setTagOptions] = useState([]);

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // URD / Old Purchase Modal & payload states
  const [oldPurchases, setOldPurchases] = useState([]);
  const [urdModalState, setUrdModalState] = useState({
    isOpen: false,
    payMode: "old_gold",
    row: null,
  });

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");

  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
    area: "",
  });

  const { states, cities, selectedState, setSelectedState } =
    useIndiaStateCity();

  // Add new state for tracking if customer is being added manually

  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);
  const [billedTo, setBilledTo] = useState(true);
  const [billedMobile, setBilledMobile] = useState("");
  const [billedMobileSearchText, setBilledMobileSearchText] = useState("");
  const [billedName, setBilledName] = useState("");
  const [billedNameSearchText, setBilledNameSearchText] = useState("");
  const [billedAddress, setBilledAddress] = useState("");
  const [billedCity, setBilledCity] = useState("");
  const [billedSelectedCustomerId, setBilledSelectedCustomerId] = useState(null);
  const [billedSaveSuccess, setBilledSaveSuccess] = useState(false);
  const [billedDetails, setBilledDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
    area: "",
  });
  const [billedSelectedState, setBilledSelectedState] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [userss, setUserss] = useState([]);

  const [billTypes, setBillTypes] = useState([]);
  const [selectedBillTypeId, setSelectedBillTypeId] = useState("");
  const [selectedBillTypeName, setSelectedBillTypeName] = useState("GST");
  const selectedBillTypeIdRef = useRef("");
  const [gstPercentage, setGstPercentage] = useState("3");

  useEffect(() => {
    selectedBillTypeIdRef.current = selectedBillTypeId;
  }, [selectedBillTypeId]);

  const applyBillTypeSelection = (billType) => {
    const id = billType?.id != null ? String(billType.id) : "";
    const name = String(billType?.name || "").trim();
    setSelectedBillTypeId(id);
    setSelectedBillTypeName(name || "GST");
    setGstPercentage("3");
  };

  useEffect(() => {
    const fetchBillTypeOptions = async () => {
      try {
        const response = await listBillTypes();
        const rows = Array.isArray(response?.data?.bill_types)
          ? response.data.bill_types
          : [];
        if (!isErpApiSuccess(response) || !Array.isArray(rows)) {
          return;
        }

        const mapped = rows.map((row) => ({
          id: row?.id,
          name: String(row?.name || "").trim(),
        }));
        setBillTypes(mapped);

        if (!selectedBillTypeIdRef.current) {
          const gstType =
            mapped.find((item) => item.name.toLowerCase() === "gst") ||
            mapped[0];
          if (gstType) {
            applyBillTypeSelection(gstType);
          }
        }
      } catch (error) {
        console.error("Error fetching bill types:", error);
      }
    };

    fetchBillTypeOptions();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) return;

    const nextSearch = [customerMobileSearchText, customerNameSearchText]
      .map((value) => (value ? String(value).trim() : ""))
      .find(
        (value) =>
          value.length >= 3 &&
          !customerSearchCacheRef.current.has(value.toLowerCase()),
      );

    if (!nextSearch) return;

    fetchUserss(nextSearch);
  }, [customerMobileSearchText, customerNameSearchText, selectedCustomerId]);

  useEffect(() => {
    if (billedTo || billedSelectedCustomerId) return;

    const nextSearch = [billedMobileSearchText, billedNameSearchText]
      .map((value) => (value ? String(value).trim() : ""))
      .find(
        (value) =>
          value.length >= 3 &&
          !customerSearchCacheRef.current.has(value.toLowerCase()),
      );

    if (!nextSearch) return;

    fetchUserss(nextSearch);
  }, [
    billedTo,
    billedMobileSearchText,
    billedNameSearchText,
    billedSelectedCustomerId,
  ]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);

  const salesFocusFields = useMemo(
    () => [
      {
        id: "sales-customer-mobile",
        group: "header",
        getElement: () =>
          document.querySelector("input[placeholder*='mobile' i]"),
      },
      {
        id: "sales-customer-name",
        group: "header",
        getElement: () =>
          document.querySelector("input[placeholder*='name' i]"),
      },
      {
        id: "sales-tag-no",
        group: "items",
        getElement: () => document.querySelector("input[placeholder*='tag' i]"),
      },
    ],
    [],
  );

  useFocusScope(`sales:${location.pathname}`, salesFocusFields);
  useRegisterShortcuts(
    desktopEnv.isDesktop
      ? [
        {
          shortcut: "mod+enter",
          commandId: "form.save",
          scope: `page:${location.pathname}`,
        },
        {
          shortcut: "mod+shift+enter",
          commandId: "form.saveAndNew",
          scope: `page:${location.pathname}`,
        },
      ]
      : [],
  );
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const CHARGE_TYPES = [
    {
      name: "Hallmark Charges",
      codes: ["HC", "HM"],
    },
    {
      name: "Stone Charges",
      codes: ["SC"],
    },
    {
      name: "Diamond Charges",
      codes: ["DC"],
    },
    {
      name: "Jadtar Charges",
      codes: ["JC"],
    },
    {
      name: "Customization Charges",
      codes: ["CC"],
    },
    {
      name: "Engraving Charges",
      codes: ["EC"],
    },
    {
      name: "Polishing Charges",
      codes: ["PC"],
    },
    {
      name: "Electroplating Charges",
      codes: ["EPC"],
    },
    {
      name: "Antique/Oxidized Finish Charges",
      codes: ["AFC"],
    },
    {
      name: "Kadha/Hollow Jewellery Filling Charges",
      codes: ["KFC"],
    },
    {
      name: "Melting & Refining Charges",
      codes: ["MRC"],
    },
    {
      name: "Weighing & Testing Charges",
      codes: ["WTC"],
    },
    {
      name: "Laser Cutting Charges",
      codes: ["LCC"],
    },
    {
      name: "Handmade Labour Charges",
      codes: ["HLC"],
    },
    {
      name: "Antique Kundan Meena Charges",
      codes: ["AKMC"],
    },
    {
      name: "Threading & Bead Stringing Charges",
      codes: ["TBC"],
    },
    {
      name: "Enamel Meenakari Charges",
      codes: ["EMC"],
    },
    {
      name: "Embossing & Carving Charges",
      codes: ["ECC"],
    },
    {
      name: "Rhodium Charges",
      codes: ["RC"],
    },
  ];

  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = CHARGE_TYPES.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery),
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = CHARGE_TYPES.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery)),
      );
    }

    // Get matches by name
    const matchedByName = CHARGE_TYPES.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name),
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`,
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Sync pool balance when navigating product group dropdown
  useEffect(() => {
    if (
      activeDropdown.type !== "productGroup" ||
      activeDropdown.row === null ||
      activeDropdown.col === null
    ) {
      return;
    }
    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    const filtered = getFilteredItems(cell?.value, "productGroup");
    if (filtered[highlightedIndex]) {
      applyPoolHeaderFromGroup(filtered[highlightedIndex]);
    }
  }, [highlightedIndex, activeDropdown, dropdownData.productGroup]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Helper function to calculate and update itRate for a row based on product group
  const calculateAndUpdateItRate = (tableData, rowIndex) => {
    let newTableData = [...tableData];
    const productGroupCell = newTableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup",
    );

    if (!productGroupCell || !productGroupCell.value) {
      return newTableData;
    }

    // Find the selected product group object to get its purity
    let selectedProductGroup = findProductGroupMatch(productGroupCell.value);

    // Fallback: infer product group metadata from cell text if not in loaded options
    if (!selectedProductGroup) {
      const cellText = String(productGroupCell.value || "").trim();
      const isGold = /gold|carat|ct|\bkt\b/i.test(cellText);
      const isSilver = /silver/i.test(cellText);
      if (isGold || isSilver) {
        selectedProductGroup = {
          label: cellText,
          name: cellText,
          metal: isGold ? "Gold" : "Silver",
          purity: "",
        };
      }
    }

    if (selectedProductGroup) {
      let computedRatePerGram = 0;
      const labelStr = `${selectedProductGroup.label || ""} ${selectedProductGroup.name || ""} ${productGroupCell.value || ""}`.toLowerCase();
      const isGold =
        selectedProductGroup.metal === "Gold" ||
        (!selectedProductGroup.metal && /gold|carat|ct|\bkt\b/i.test(labelStr));

      if (isGold) {
        if (Array.isArray(masterGoldRate)) {
          const purityStr = (selectedProductGroup.purity || "").toString();
          const purityNum = parseFloat(purityStr);
          let idx = 0;
          if (labelStr.includes("rose")) {
            idx = 3;
          } else if (!isNaN(purityNum)) {
            const p = Math.round(purityNum);
            if (p === 22) idx = 1;
            else if (p === 18) idx = 2;
            else if (p === 9) idx = 4;
            else idx = 0;
          } else {
            if (/\b22\s*(carat|ct|k)\b|\b22\b/.test(labelStr)) idx = 1;
            else if (/\b18\s*(carat|ct|k)\b|\b18\b/.test(labelStr)) idx = 2;
            else if (/\b9\s*(carat|ct|k)\b|\b9\b/.test(labelStr)) idx = 4;
            else idx = 0;
          }
          const ratePerGram = Number(masterGoldRate[idx] || 0);
          computedRatePerGram = ratePerGram;
        } else {
          // Live rate: `masterGoldRate` is a single fine-gold quote in ₹ per 10g (footer value).
          // Convert to ₹/g, then apply karat: rate_per_g = (quote/10) * (karat/24).
          // DB / daily-price mode uses the array branch above (already per-gram per carat).
          const baseQuote = Number(masterGoldRate) || 0;
          const fineGoldPerGram =
            showLiveRate && !Array.isArray(masterGoldRate)
              ? baseQuote / 10
              : baseQuote;
          const pgLabelHint = `${selectedProductGroup.label || ""} ${productGroupCell.value || ""
            }`.trim();
          if (selectedProductGroup.purity) {
            computedRatePerGram = calculatePurityBasedRate(
              selectedProductGroup.purity,
              fineGoldPerGram,
              "Gold",
              pgLabelHint,
            );
          } else {
            computedRatePerGram = calculatePurityBasedRate(
              "",
              fineGoldPerGram,
              "Gold",
              pgLabelHint,
            );
          }
        }
      } else {
        const baseRate = masterSilverRate;
        const pgLabelHint = `${selectedProductGroup.label || ""} ${productGroupCell.value || ""
          }`.trim();
        if (selectedProductGroup.purity) {
          computedRatePerGram = calculatePurityBasedRate(
            selectedProductGroup.purity,
            baseRate,
            "Silver",
            pgLabelHint,
          );
        } else {
          computedRatePerGram = calculatePerGramRate(baseRate, "Silver");
        }
      }

      // Find and update the itRate cell
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRate",
      );

      if (itRateCell) {
        const isOverridden = newTableData[rowIndex].userOverrodeItRate;
        const effectiveRate = isOverridden
          ? parseFloat(itRateCell.value) || 0
          : computedRatePerGram;
        if (!isOverridden) {
          itRateCell.value = computedRatePerGram.toFixed(2);
        }

        // Recalculate itAmt after updating/using effective itRate
        const itRateOnCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itRateOn",
        );
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt",
        );
        const grWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "grWt",
        );

        if (itRateOnCell && (netWtCell || grWtCell)) {
          const itRateOn = itRateOnCell.value || "NWt";
          const netWt = parseFloat(netWtCell?.value) || 0;
          const grWt = parseFloat(grWtCell?.value) || 0;

          let baseWeight = itRateOn === "GWt" ? grWt : netWt;

          const itAmt = (baseWeight * effectiveRate).toFixed(2);
          const itAmtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itAmt",
          );
          if (itAmtCell) {
            itAmtCell.value = itAmt.toString();
          }

          // Recalculate total amount
          calculateTotalAmount(newTableData, rowIndex);
        }
      }
    }

    return newTableData;
  };

  // Function to calculate Labor Amount using Lbr % (based on itRate per gram)
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    itRatePerGram,
  ) => {
    return (
      (lbrPercentage / 100) * baseWeight * (parseFloat(itRatePerGram) || 0)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  const clearManualAmountOverrides = (row) => {
    if (!row?.data) return { lbrCleared: false, totalCleared: false };

    const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
    let lbrCleared = false;
    if (lbrAmtCell?.isManual) {
      delete lbrAmtCell.isManual;
      lbrCleared = true;
    }

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    let totalCleared = false;
    if (totalAmtCell?.totalAmountDetails?.isManual) {
      delete totalAmtCell.totalAmountDetails;
      totalCleared = true;
    }

    return { lbrCleared, totalCleared };
  };

  // Function to reverse calculate labor details from Total Amount
  const reverseCalculateLabor = (row, newTotalAmt) => {
    const itAmt =
      parseFloat(row.data.find((c) => c.id === "itAmt")?.value) || 0;
    const othAmt =
      parseFloat(row.data.find((c) => c.id === "othAmt")?.value) || 0;

    // 1. Calculate lbrAmt
    const lbrAmt = Math.max(newTotalAmt - itAmt - othAmt, 0);

    // Update lbrAmt cell
    const lbrAmtCell = row.data.find((c) => c.id === "lbrAmt");
    if (lbrAmtCell) {
      lbrAmtCell.value = lbrAmt.toFixed(2);
      if (lbrAmtCell.isManual) {
        delete lbrAmtCell.isManual;
      }
    }

    // 2. Get Base Weight
    const lTypeCell = row.data.find((c) => c.id === "lType");
    const lType = (lTypeCell?.value || "").toString().trim().toUpperCase();
    let baseWeight = 0;

    if (lType === "NWT") {
      baseWeight =
        parseFloat(row.data.find((c) => c.id === "netWt")?.value) || 0;
    } else if (lType === "GWT") {
      baseWeight =
        parseFloat(row.data.find((c) => c.id === "grWt")?.value) || 0;
    } else if (lType === "PCS") {
      baseWeight = parseFloat(row.data.find((c) => c.id === "pcs")?.value) || 0;
    }

    // 3. Calculate lbrRate
    let lbrRate = 0;
    if (baseWeight > 0) {
      lbrRate = lbrAmt / baseWeight;
    }

    // Update lbrRate cell
    const lbrRateCell = row.data.find((c) => c.id === "lbrRate");
    if (lbrRateCell) {
      lbrRateCell.value = lbrRate.toFixed(2);
    }

    // 4. Calculate lbrPercentage
    const itRateCell = row.data.find((c) => c.id === "itRate");
    const itRate = parseFloat(itRateCell?.value) || 0;
    let lbrPercentage = 0;

    // Calculate percentage based on rates if available
    if (itRate > 0) {
      lbrPercentage = (lbrRate / itRate) * 100;
    }
    // Fallback: try using amounts if rate is missing but amount exists
    else if (itAmt > 0 && baseWeight > 0) {
      const implicitRate = itAmt / baseWeight;
      if (implicitRate > 0) {
        lbrPercentage = (lbrRate / implicitRate) * 100;
      }
    }

    // Update lbrPercentage cell
    const lbrPercentageCell = row.data.find((c) => c.id === "lbrPercentage");
    if (lbrPercentageCell) {
      lbrPercentageCell.value = lbrPercentage.toFixed(2);
    }
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value) => {
    let newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;

    // Prevent changes to tagNo when coming from custom order
    if (id === "tagNo" && customOrderId) {
      return; // Don't allow changes to tag number from custom order
    }

    // Store previous value to check if it has changed
    const previousValue = cell.value;

    // Allow manual override of itRate; mark flag and recalc itAmt
    if (id === "itRate") {
      cell.value = value;
      newTableData[rowIndex].userOverrodeItRate = true;
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      const itRate = parseFloat(value) || 0;
      const itRateOnCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itRateOn",
      );
      const netWtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "netWt",
      );
      const grWtCell = newTableData[rowIndex].data.find((c) => c.id === "grWt");
      const itAmtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itAmt",
      );
      if (itAmtCell && (netWtCell || grWtCell)) {
        const itRateOn = itRateOnCell?.value || "NWt";
        const netWt = parseFloat(netWtCell?.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const baseWeight = itRateOn === "GWt" ? grWt : netWt;
        itAmtCell.value = (baseWeight * itRate).toFixed(2);
        calculateTotalAmount(newTableData, rowIndex);
      }
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
      setTableData(newTableData);
      return;
    }

    // If the changed column is lbrType
    if (id === "lbrType") {
      cell.value = value;

      // If Fixed is selected, clear lbrPercentage and lbrRate
      if (value === "Fixed") {
        const lbrPercentageCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrPercentage",
        );
        const lbrRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrRate",
        );

        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
      }

      setTableData(newTableData);
      return;
    }

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      design: /^[a-zA-Z0-9\s\-\_]*$/, // Allow alphanumeric, spaces, hyphens, underscores
      size: /^\d*$/, // Only numbers
      pcs: /^\d*$/, // Only numbers
      lbrPercentage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      mrp: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      totalAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      return; // Don't update if validation fails
    }

    // Add validation for pcs field
    if (id === "pcs") {
      const tagNoCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "tagNo",
      );
      if (tagNoCell && tagNoCell.value) {
        // Find the current_pcs value from the API response
        const currentPcs = parseInt(cell.current_pcs) || 0;
        const enteredPcs = parseInt(value) || 0;

        if (enteredPcs > currentPcs) {
          toast.error(`Cannot enter more than ${currentPcs} pieces`);
          return; // Don't update if validation fails
        }
      }
    }

    // Handle tagNo: fetch options when user types 3+ digits
    if (id === "tagNo") {
      cell.value = value;
      setTableData(newTableData);

      // Trigger API call when user types 3+ digits
      if (value && value.length >= 3) {
        // Make API call for each digit after 3 (111, 1111, 11111, etc.)
        // Only reset if the new value doesn't start with the last searched prefix
        // This ensures we make a new call when user continues typing
        if (!lastSearchedTagPrefix || value !== lastSearchedTagPrefix) {
          setTagNoApiCalled(false); // Reset flag before API call
          fetchTagNoByPrefix(value);
        }
      } else {
        // Clear dropdown if less than 3 digits
        tagNoSearchRequestIdRef.current += 1;
        setDropdownData((prev) => ({
          ...prev,
          tagNo: [],
        }));
        setLastSearchedTagPrefix("");
        setTagNoApiCalled(false); // Reset flag when clearing
        // Close dropdown if open
        setActiveDropdown({ row: null, col: null, type: null });
      }
      return;
    }

    // Handle productGroup: fetch options when user types 3+ characters
    if (id === "productGroup") {
      cell.value = value;
      // Typing invalidates a previously selected id until dropdown select
      delete newTableData[rowIndex].erpProductGroupId;
      if (cell.productGroupId != null) delete cell.productGroupId;
      setTableData(newTableData);

      // Trigger API call when user types 3+ characters
      if (value && value.length >= 3) {
        // Only reset if the new value doesn't start with the last searched prefix
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false); // Reset flag before API call
          fetchProductGroupByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, productGroup: [] }));
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
        setProductGroupPoolHeader(null);
      }
      return;
    }

    // Allow manual Total Amount entry when live rate is off
    if (id === "totalAmt") {
      cell.value = value;
      if (!showLiveRate) {
        if (!cell.totalAmountDetails) {
          cell.totalAmountDetails = {};
        }
        cell.totalAmountDetails.isManual = true;
      }

      // Calculate reverse labor details
      const newTotalAmt = parseFloat(value) || 0;
      reverseCalculateLabor(newTableData[rowIndex], newTotalAmt);

      setTableData(newTableData);
      return;
    }

    cell.value = value;

    const shouldClearManualOverrides = [
      "lType",
      "lbrPercentage",
      "lbrRate",
      "grWt",
      "lsWt",
      "netWt",
      "pcs",
      "itRateOn",
      "itRate",
      "othAmt",
    ].includes(id);

    if (shouldClearManualOverrides) {
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
    }

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt",
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt",
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt",
        );
        if (netWtCell) {
          netWtCell.value = netWt.toString();

          // If itRate exists, recalculate itAmt based on itRateOn
          const itRateCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRate",
          );
          const itRateOnCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRateOn",
          );

          if (itRateCell && itRateCell.value) {
            const itRate = parseFloat(itRateCell.value) || 0;
            const itRateOn = itRateOnCell?.value; // No default value

            // Determine which weight to use based on itRateOn
            let baseWeight = 0; // Initialize to 0, no default
            if (itRateOn === "NWt") {
              baseWeight = netWt;
            } else if (itRateOn === "GWt") {
              baseWeight = grWt;
            }

            const itAmt = (baseWeight * itRate).toFixed(2);

            // Update the itAmt cell
            const itAmtCell = newTableData[rowIndex].data.find(
              (cell) => cell.id === "itAmt",
            );
            if (itAmtCell) {
              itAmtCell.value = itAmt.toString();

              // Recalculate totalAmt
              calculateTotalAmount(newTableData, rowIndex);
            }
          }
        }
      }
    } else if (id === "netWt" || id === "grWt" || id === "itRateOn") {
      // Note: itRate is removed from manual input - it's auto-calculated
      // Recalculate itRate first (to ensure it's up to date), then calculate itAmt
      const updatedData = calculateAndUpdateItRate(newTableData, rowIndex);
      // Update the state with the updated data
      newTableData = updatedData;
    } else if (id === "lType") {
      // Handle LBR type change
      const lTypeCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lType",
      );
      if (lTypeCell) {
        lTypeCell.value = value;
      }
    } else if (
      id === "lbrAmt" ||
      id === "othAmt" ||
      id === "itAmt" ||
      id === "mrp"
    ) {
      // Recalculate totalAmt when any of the amount fields change
      calculateTotalAmount(newTableData, rowIndex);
    }

    // Handle LBR percentage and rate calculations
    if (id === "lType" || id === "lbrPercentage" || id === "lbrRate") {
      // Track which labor field was last changed
      if (id === "lbrPercentage" || id === "lbrRate") {
        newTableData[rowIndex].lastChangedLaborField = id;
      }
      handleLaborCalculations(newTableData, rowIndex);
    }

    setTableData(newTableData);

    // REMOVED THE PROBLEMATIC CODE THAT WAS CALLING sendTagNoToApi ON EVERY KEYSTROKE
    // The API call should only happen when selecting from dropdown (handled in handleDropdownSelect)

    // If you need to handle barcode scanning, you might want to add a separate function
    // or detect when the input is coming from a barcode scanner vs manual typing
  };

  // Add this function to handle key events on input fields
  const handleKeyPress = (event, rowIndex, colIndex) => {
    // Check if this is a tag number field and Enter key is pressed
    const columnId = COLUMNS[colIndex]?.id;
    if (columnId === "tagNo" && event.key === "Enter") {
      // Don't process tagNo actions when coming from custom order
      if (isQueueLocked) {
        return;
      }
      const value = event.target.value;
      if (value) {
        // Prevent duplicate processing of the same tag number in quick succession
        const now = Date.now();
        if (
          lastProcessedTagNo.value !== value ||
          now - lastProcessedTagNo.timestamp > 1000
        ) {
          // Update last processed tag info
          lastProcessedTagNo = {
            value: value,
            timestamp: now,
          };

          // If we're at the last row, add a new row
          if (rowIndex === tableData.length - 1 && !window.isAddingRow) {
            setTimeout(() => {
              handleAddRow();
            }, 100);
          }
        }
      }
    }
  };

  // New function to calculate total amount
  const calculateTotalAmount = (tableData, rowIndex) => {
    const itAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "itAmt",
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt",
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "othAmt",
    );
    const mrpCell = tableData[rowIndex].data.find((cell) => cell.id === "mrp");

    const itAmt = parseFloat(itAmtCell?.value) || 0;
    const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;
    const mrp = parseFloat(mrpCell?.value) || 0;

    let totalAmtValue = 0;
    if (mrp > 0) {
      totalAmtValue = mrp;
    } else {
      totalAmtValue = itAmt + lbrAmt + othAmt;
    }

    const totalAmt = roundLineTotalAmount(totalAmtValue);

    // Update the totalAmt cell
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmt",
    );
    if (totalAmtCell) {
      if (!totalAmtCell.totalAmountDetails?.isManual) {
        totalAmtCell.value = totalAmt.toString();
        if (totalAmtCell.totalAmountDetails) {
          delete totalAmtCell.totalAmountDetails;
        }
      }
    }
  };

  // Handle image upload
  const handleImageUpload = async (field, rowIndex) => {
    // First create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image",
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field;
          }
        }
        return updatedData;
      });

      // Image upload disabled — keep local file name as value

      // Update the image name in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image",
          );
          if (imageCell) {
            imageCell.value = field?.name || "";
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Open image modal
  const openImageModal = (imageUrl) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image",
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal"),
    );
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: getDefaultCellValue(col.id),
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);

    // Clear tagNo dropdown data and reset flags when adding new row
    setDropdownData((prev) => ({
      ...prev,
      tagNo: [],
    }));
    setLastSearchedTagPrefix("");
    setTagNoApiCalled(false);
    setActiveDropdown({ row: null, col: null, type: null });
    tagNoSearchRequestIdRef.current += 1;

    // Focus on the tagNo field of the newly added row
    setTimeout(() => {
      const tagNoColumnIndex = COLUMNS.findIndex((col) => col.id === "tagNo");
      if (tagNoColumnIndex !== -1) {
        const tagNoInput =
          inputRefs.current[`${newRowIndex}-${tagNoColumnIndex}`];
        if (tagNoInput) {
          tagNoInput.focus();
          // Clear any existing value in the input field
          tagNoInput.value = "";
        }
      }
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
      // Reset the flag after the row is added and focus is set
      window.isAddingRow = false;
    }, 150); // Increased timeout to ensure DOM is updated
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    const columnId = COLUMNS[colIndex]?.id;
    // Ensure we're passing the label or value, not the entire object
    const value =
      typeof selectedItem === "object"
        ? selectedItem.name || selectedItem.label || ""
        : selectedItem;

    if (columnId === "productGroup" && typeof selectedItem === "object") {
      productGroupOptionsRef.current = mergeProductGroupOptions(
        productGroupOptionsRef.current,
        [selectedItem],
      );
      applyPoolHeaderFromGroup(selectedItem);
      const selectedId = selectedItem.value ?? selectedItem.id;

      setTableData((prevData) => {
        const updated = prevData.map((row, idx) => {
          if (idx !== rowIndex) return row;
          const data = row.data.map((cell) =>
            cell.id === "productGroup"
              ? {
                ...cell,
                value,
                ...(selectedId != null && selectedId !== ""
                  ? { productGroupId: selectedId }
                  : {}),
              }
              : cell,
          );
          const nextRow = { ...row, data };
          if (selectedId != null && selectedId !== "") {
            nextRow.erpProductGroupId = selectedId;
          } else {
            delete nextRow.erpProductGroupId;
          }
          return nextRow;
        });
        return calculateAndUpdateItRate(updated, rowIndex);
      });

      setActiveDropdown({ row: null, col: null, type: null });
      setTimeout(() => {
        const nextColIndex = colIndex + 1;
        if (nextColIndex < COLUMNS.length) {
          const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
          if (nextInput) nextInput.focus();
        } else {
          focusFirstCellOfNextRow(rowIndex);
        }
      }, 0);
      return;
    }

    // Tag No selected from list — close menu, cancel in-flight searches, fill row
    if (columnId === "tagNo" && value && selectedItem) {
      tagNoSearchRequestIdRef.current += 1;
      setActiveDropdown({ row: null, col: null, type: null });
      setDropdownData((prev) => ({
        ...prev,
        tagNo: [],
      }));
      setLastSearchedTagPrefix(String(value));
      setTagNoApiCalled(false);

      // Update cell without going through handleInputChange (avoids re-fetch/re-open)
      setTableData((prevData) => {
        const newTableData = prevData.map((row, idx) => {
          if (idx !== rowIndex) return row;
          return {
            ...row,
            data: row.data.map((cell) =>
              cell.id === "tagNo" ? { ...cell, value } : cell,
            ),
          };
        });
        return newTableData;
      });

      sendTagNoToApi(value, rowIndex);

      // Only add a new row if this is the last row and the value was selected from dropdown
      if (rowIndex === tableData.length - 1) {
        setTimeout(() => {
          handleAddRow();
        }, 200);
      }

      return;
    }

    handleInputChange(rowIndex, colIndex, value);
    setActiveDropdown({ row: null, col: null, type: null });

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Track the last processed tag number to prevent duplicate processing
  let lastProcessedTagNo = {
    value: null,
    timestamp: 0,
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "lType" || columnType === "itRateOn") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText),
      );
    }

    // Special case: productGroup — API search results (no client-side re-filter)
    if (columnType === "productGroup") {
      return dropdownData[columnType] || [];
    }

    // Special case: tagNo (search only label)
    if (columnType === "tagNo") {
      return dropdownData[columnType].filter(
        (item) =>
          item.label && item.label.toLowerCase().includes(normalizedSearchText),
      );
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return String(item).toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  const getFilteredTagNoOptions = (searchText) => {
    if (!searchText) return tagOptions;

    return tagOptions.filter(
      (item) =>
        item.label.toLowerCase().includes(searchText.toLowerCase()) ||
        item.short_code?.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex],
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);
    // Reset refs array
    dropdownItemsRef.current = [];

    // Special styling for productGroup dropdown - match AddGirvi.jsx pattern
    const isProductGroup = activeDropdown.type === "productGroup";
    const isTagNo = activeDropdown.type === "tagNo";

    // Don't render an empty white shell (height 0 + bootstrap padding)
    const showSearching = isProductGroup && productGroupSearchLoading;
    const showNoMatch =
      filtered.length === 0 &&
      ((isTagNo && tagNoApiCalled) ||
        (isProductGroup && productGroupApiCalled && !productGroupSearchLoading));
    if (filtered.length === 0 && !showSearching && !showNoMatch) {
      return null;
    }

    // For productGroup: use 5 items with 40px height each (matching AddGirvi.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT2 = isProductGroup ? 5 : 5;
    const ITEM_HEIGHT2 = isProductGroup ? 40 : 35;
    const visibleItems2 =
      filtered.length > 0
        ? Math.min(filtered.length, VISIBLE_COUNT2)
        : 1; // room for "Searching..." / "No matching items"
    const dropdownHeight2 = visibleItems2 * ITEM_HEIGHT2;

    // Calculate max width based on content (for non-productGroup, non-tagNo dropdowns)
    const maxContentWidth =
      filtered.length > 0
        ? Math.max(
          ...filtered.map((item) => {
            const tempSpan = document.createElement("span");
            tempSpan.style.fontSize = "0.85rem";
            tempSpan.style.visibility = "hidden";
            tempSpan.style.position = "absolute";
            tempSpan.style.whiteSpace = "nowrap";

            // Safely get the label
            const itemLabel =
              typeof item === "object" ? item.label || "" : item;
            tempSpan.textContent = itemLabel;

            document.body.appendChild(tempSpan);
            const width = tempSpan.offsetWidth;
            document.body.removeChild(tempSpan);
            return width;
          }),
          dropdownPosition.width, // minimum width is input width
        )
        : dropdownPosition.width || 100;

    // Calculate width for dropdowns - match AddGirvi.jsx pattern
    let adjustedWidth;
    if (isProductGroup) {
      // ProductGroup: match AddGirvi.jsx - Math.max(inputWidth, 200)
      adjustedWidth = Math.max(200, dropdownPosition.width);
    } else if (isTagNo) {
      // TagNo: wide enough for full tags (e.g. PRO@115103); prefer content width vs narrow column
      adjustedWidth = Math.max(
        100,
        maxContentWidth + 28,
        dropdownPosition.width,
      );
    } else {
      // Other dropdowns: use content-based width
      adjustedWidth = Math.max(maxContentWidth + 10, 100);
    }

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          maxHeight: isProductGroup
            ? `${dropdownHeight2 + 16}px`
            : `${dropdownHeight2}px`,
          height: isProductGroup
            ? `${dropdownHeight2 + 16}px`
            : `${dropdownHeight2}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
          padding: 0,
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight2}px`,
            height: `${dropdownHeight2}px`,
            overflowY: filtered.length > VISIBLE_COUNT2 ? "auto" : "hidden",
            overflowX: isTagNo ? "auto" : "hidden",
          }}
        >
          {isProductGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? item.name ||
                    String(item.label || "")
                      .split(" — pool:")[0]
                      .trim()
                    : item.name || item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  title={itemLabel}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isProductGroup ? "normal" : "nowrap",
                    wordWrap: isProductGroup ? "break-word" : "normal",
                    overflow: isProductGroup || isTagNo ? "visible" : "hidden",
                    textOverflow:
                      isProductGroup || isTagNo ? "clip" : "ellipsis",
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(idx);
                    if (isProductGroup) applyPoolHeaderFromGroup(item);
                  }}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item,
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : isProductGroup && !productGroupApiCalled ? null : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal"),
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    // For tagNo and productGroup, only show dropdown if user has typed 3+ characters and we have results
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "tagNo") {
        // For tagNo, only show dropdown if user has typed 3+ digits AND we have results or API was called
        const tagNoCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "tagNo",
        );
        const tagNoValue = (tagNoCell?.value || "").toString().trim();
        const hasEnoughDigits = tagNoValue.length >= 3;
        const hasResults = getFilteredItems(tagNoValue, "tagNo").length > 0;

        // Only show dropdown if user has typed 3+ digits AND (has results OR API was called)
        if (hasEnoughDigits && (hasResults || tagNoApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ digits
      } else if (columnType === "productGroup") {
        // For productGroup, only show dropdown if user has typed 3+ characters AND we have results or API was called
        const productGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "productGroup",
        );
        const productGroupValue = (productGroupCell?.value || "")
          .toString()
          .trim();
        const hasEnoughChars = productGroupValue.length >= 3;
        if (!hasEnoughChars && productGroupValue) {
          const selected = findProductGroupMatch(productGroupValue);
          applyPoolHeaderFromGroup(selected);
        }
        const hasResults =
          getFilteredItems(productGroupValue, "productGroup").length > 0;

        // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
        if (hasEnoughChars && (hasResults || productGroupApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ characters
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  const handleTotalAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openTotalAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        totalAmountEnterPress.rowIndex === rowIndex &&
        now - totalAmountEnterPress.time < 500
      ) {
        event.preventDefault();
        openTotalAmountModal(rowIndex);
        setTotalAmountEnterPress({ time: 0, rowIndex: null });
      } else {
        setTotalAmountEnterPress({ time: now, rowIndex });
      }
    }
  };

  const openTotalAmountModal = (rowIndex) => {
    const row = tableData[rowIndex];
    if (!row) return;

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const currentDetails = totalAmtCell?.totalAmountDetails;

    const currentAmount = currentDetails?.inclusiveAmount
      ? String(currentDetails.inclusiveAmount)
      : totalAmtCell &&
        totalAmtCell.value !== undefined &&
        totalAmtCell.value !== null
        ? String(totalAmtCell.value)
        : "";

    const currentGst = currentDetails?.gst ?? totalAmtCell?.gst ?? "3";

    setTotalAmountModal({
      isOpen: true,
      rowIndex,
      amount: currentAmount,
      gst: String(currentGst || "3"),
    });
  };

  const closeTotalAmountModal = (shouldRefocus = false) => {
    const { rowIndex } = totalAmountModal;

    setTotalAmountModal({
      isOpen: false,
      rowIndex: null,
      amount: "",
      gst: "3",
    });

    if (shouldRefocus && rowIndex !== null) {
      const totalAmtColIndex = COLUMNS.findIndex(
        (col) => col.id === "totalAmt",
      );
      if (totalAmtColIndex !== -1) {
        setTimeout(() => {
          const inputElement =
            inputRefs.current[`${rowIndex}-${totalAmtColIndex}`];
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 0);
      }
    }
  };

  const handleSaveTotalAmount = () => {
    const { rowIndex, amount, gst } = totalAmountModal;
    if (rowIndex === null) {
      closeTotalAmountModal();
      return;
    }

    const sanitizedAmount =
      typeof amount === "string" ? amount.trim() : String(amount || "");

    const gstPercentage = parseFloat(gst) || 0;
    const numericAmount = parseFloat(sanitizedAmount);
    const hasValidAmount = sanitizedAmount !== "" && !isNaN(numericAmount);

    const divisor = 1 + gstPercentage / 100;
    const baseAmount =
      hasValidAmount && divisor !== 0
        ? (numericAmount / divisor).toFixed(2)
        : "";
    const inclusiveFormatted = hasValidAmount ? numericAmount.toFixed(2) : "";

    setTableData((prevData) => {
      const newData = [...prevData];

      // Deep clone the row data to ensure we don't mutate state directly before returning
      const rowData = newData[rowIndex].data.map((cell) => ({ ...cell }));
      const newRow = { ...newData[rowIndex], data: rowData };

      const totalAmtCell = newRow.data.find((cell) => cell.id === "totalAmt");

      if (totalAmtCell) {
        if (!hasValidAmount) {
          totalAmtCell.value = "";
          if (totalAmtCell.totalAmountDetails) {
            delete totalAmtCell.totalAmountDetails;
          }
        } else {
          totalAmtCell.value = baseAmount;
          totalAmtCell.totalAmountDetails = {
            gst,
            inclusiveAmount: inclusiveFormatted,
            baseAmount,
            isManual: true,
          };
        }
      }

      // Reverse calculate labor if we have a valid amount
      if (hasValidAmount && baseAmount !== "") {
        reverseCalculateLabor(newRow, parseFloat(baseAmount));
      }

      newData[rowIndex] = newRow;
      return newData;
    });

    closeTotalAmountModal(true);
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
          ...entry,
          rateOn: entry.rateOn || "", // Ensure rateOn is initialized
        }))
        : currentValue
          ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get all required values
      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrPercentageCell = row.data.find(
        (cell) => cell.id === "lbrPercentage",
      );
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");
      const itRateOnCell = row.data.find((cell) => cell.id === "itRateOn");
      const productGroupCell = row.data.find(
        (cell) => cell.id === "productGroup",
      );

      const grWt = parseFloat(grWtCell?.value) || 0;
      const lType = (lTypeCell?.value || "").toString().trim();
      const lTypeUpper = lType.toUpperCase(); // Normalize for comparison
      const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
      const lbrRate = parseFloat(lbrRateCell?.value) || 0;
      const itRate = parseFloat(itRateCell?.value) || 0;
      const itRateOn = (itRateOnCell?.value || "").toString().trim();
      const itRateOnUpper = itRateOn.toUpperCase(); // Normalize for comparison

      // Get metal type from product group
      const selectedProductGroup = findProductGroupMatch(
        productGroupCell?.value,
      );
      const metalType = selectedProductGroup?.metal || "Gold";

      // Update Other Amount
      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      // Update Less Weight
      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      if (lsWtCell) {
        lsWtCell.value = formatLessWeight(totalLessWeight);
      }

      // Calculate and update Net Weight
      const netWeight = calculateNetWeight(grWt, totalLessWeight);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      // Calculate Item Amount based on ItRateOn (case-insensitive)
      const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
      if (itRateCell && itRate && itAmtCell) {
        let baseWeight = 0;
        if (itRateOnUpper === "NWT") {
          baseWeight = netWeight;
        } else if (itRateOnUpper === "GWT") {
          baseWeight = grWt;
        }
        const itAmt = (baseWeight * itRate).toFixed(2);
        itAmtCell.value = itAmt;
      }

      // Calculate Labor Amount (case-insensitive)
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      if (lbrAmtCell && !lbrAmtCell.isManual) {
        let baseWeight = 0;
        if (lTypeUpper === "NWT") {
          baseWeight = netWeight;
        } else if (lTypeUpper === "GWT") {
          baseWeight = grWt;
        } else if (lTypeUpper === "PCS") {
          const pcsCell = row.data.find((cell) => cell.id === "pcs");
          baseWeight = parseFloat(pcsCell?.value) || 0;
        }

        let lbrAmt = 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            itRate,
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        lbrAmtCell.value = lbrAmt.toFixed(2);
      }

      // Calculate Total Amount
      const mrpCell = row.data.find((cell) => cell.id === "mrp");
      const itAmt = parseFloat(itAmtCell?.value) || 0;
      const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const mrp = parseFloat(mrpCell?.value) || 0;

      let totalAmtValue = 0;
      if (mrp > 0) {
        totalAmtValue = mrp;
      } else {
        totalAmtValue = itAmt + lbrAmt + othAmt;
      }
      const totalAmt = roundLineTotalAmount(totalAmtValue);

      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        if (!totalAmtCell.totalAmountDetails?.isManual) {
          totalAmtCell.value = totalAmt;
          if (totalAmtCell.totalAmountDetails) {
            delete totalAmtCell.totalAmountDetails;
          }
        }
      }

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  const renderTotalAmountModal = () => {
    if (!totalAmountModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={totalAmountModal.isOpen}
        onHide={() => closeTotalAmountModal(true)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-0 align-items-center">
          <Modal.Title className="fw-semibold">GST Settlement</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-2">GST (%)</Form.Label>
            <div className="d-flex gap-3 flex-wrap">
              {["3", "5", "12", "18"].map((value) => (
                <Form.Check
                  key={value}
                  type="radio"
                  name="total-amount-gst-selection"
                  id={`total-amount-gst-${value}`}
                  label={`${value}%`}
                  value={value}
                  checked={totalAmountModal.gst === value}
                  onChange={(e) =>
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      gst: e.target.value,
                    }))
                  }
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group controlId="totalAmountModal-amount">
            <div className="d-flex gap-2">
              <Form.Control
                ref={totalAmountInputRef}
                type="text"
                value={totalAmountModal.amount}
                placeholder="0.00"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      amount: value,
                    }));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveTotalAmount();
                  }
                }}
                style={{ flexBasis: "60%" }}
                className={buttonClass}
              />
              <Button
                variant="primary"
                className="px-4"
                onClick={handleSaveTotalAmount}
                style={{ flexBasis: "20%" }}
              >
                Save
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0"></Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal"),
    );
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase()),
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry,
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };

  const getRowEffectiveAmount = (row) => {
    if (!row || !row.data) return 0;

    const mrpCell = row.data.find((cell) => cell.id === "mrp");
    const mrpValue = mrpCell ? parseFloat(mrpCell.value) : NaN;
    const hasMrp =
      mrpCell && mrpCell.value !== "" && !isNaN(mrpValue) && mrpValue > 0;

    if (hasMrp) {
      return Math.round(mrpValue);
    }

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const totalAmtValue = totalAmtCell ? parseFloat(totalAmtCell.value) : NaN;

    return isNaN(totalAmtValue) ? 0 : Math.round(totalAmtValue);
  };

  const calculateRawTableTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const effectiveAmount = getRowEffectiveAmount(row);
      return sum + effectiveAmount;
    }, 0);
  };

  const calculateTotal = () => calculateRawTableTotal();

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Add this function to calculate Labor Amount
  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, metalType) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      const masterRate =
        metalType === "Gold" ? masterGoldRate : masterSilverRate;
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        masterRate,
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  // Calculate total discount amount based on discount type and value
  const calculateDiscountAmount = () => {
    const totalAmount = calculateTotal();
    if (!discountValue || isNaN(discountValue) || totalAmount <= 0) {
      return 0;
    }
    const discount = parseFloat(discountValue);
    if (discountType === "%") {
      return (totalAmount * discount) / 100;
    } else {
      return discount;
    }
  };

  // Calculate GST amount based on percentage
  const calculateGstAmount = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const taxableAmount = totalAmount - discountAmt;

    if (!gstPercentage || isNaN(gstPercentage)) {
      return {
        total: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
      };
    }

    const gstRate = parseFloat(gstPercentage);
    const totalGstAmount = (taxableAmount * gstRate) / 100;

    // Get the business state (you may need to replace this with your actual business state)
    const businessStateName = "Gujarat";

    // Debug logs
    // console.log("Customer Details:", customerDetails);
    // console.log("Customer State:", customerDetails.state);
    // console.log("Business State:", businessState);
    // console.log(
    //   "State Comparison:",
    //   customerDetails.state &&
    //     customerDetails.state.toLowerCase() === businessState.toLowerCase()
    // );

    // Check if customer state matches business state (case-insensitive comparison)
    const customerStateVal = customerDetails.state || "";
    const customerStateLower = customerStateVal.toLowerCase();
    const businessStateLower = businessStateName.toLowerCase();
    const businessStateIso2 = (
      states.find((s) => s.name.toLowerCase() === businessStateLower)?.iso2 ||
      ""
    ).toLowerCase();
    const isIntraStateTransaction =
      customerStateVal === "" ||
      customerStateLower === businessStateLower ||
      (businessStateIso2 && customerStateLower === businessStateIso2);

    if (isIntraStateTransaction) {
      // For intra-state transactions, split GST into SGST and CGST
      // SGST = (Taxable Amount × X%) ÷ 2
      // CGST = (Taxable Amount × X%) ÷ 2
      const halfGstRate = gstRate / 2;
      const sgstAmount = (taxableAmount * halfGstRate) / 100;
      const cgstAmount = (taxableAmount * halfGstRate) / 100;

      // console.log("Intra-state transaction detected. Splitting GST:");
      // console.log("SGST Amount:", sgstAmount);
      // console.log("CGST Amount:", cgstAmount);

      return {
        total: totalGstAmount,
        sgst: sgstAmount,
        cgst: cgstAmount,
        igst: 0,
      };
    } else {
      // console.log(
      //   "Inter-state transaction detected. Using IGST:",
      //   totalGstAmount
      // );
      // For inter-state transactions, apply full GST as IGST
      return {
        total: totalGstAmount,
        sgst: 0,
        cgst: 0,
        igst: totalGstAmount,
      };
    }
  };

  // Calculate amount before payment-section round-off from money-rounded
  // SGST/CGST/IGST so bill lines + round-off always sum to a whole rupee.
  const calculateAmountBeforeRoundOff = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const gstAmts = calculateGstAmount();
    const sgst = Math.round((Number(gstAmts.sgst) || 0) * 100) / 100;
    const cgst = Math.round((Number(gstAmts.cgst) || 0) * 100) / 100;
    const igst = Math.round((Number(gstAmts.igst) || 0) * 100) / 100;
    return totalAmount - discountAmt + sgst + cgst + igst;
  };

  const getAutoPaymentRoundOff = () =>
    autoRoundOff(calculateAmountBeforeRoundOff()).roundOff;

  // Calculate grand total (total amount - discount + GST + round-off adjustment)
  const calculateGrandTotal = () => {
    const amountBeforeRoundOff = calculateAmountBeforeRoundOff();
    const roundOff = getEffectiveRoundOff();
    return (amountBeforeRoundOff + roundOff).toFixed(2);
  };

  const calculateTotalPaid = () => {
    return paymentRows.reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const calculatePayableAmount = () => {
    const totalPaid = calculateTotalPaid();
    const grandTotal = parseFloat(calculateGrandTotal()) || 0;
    const paidOut = payableRows.reduce(
      (sum, row) => sum + (parseFloat(row.amount) || 0),
      0,
    );
    if (totalPaid > grandTotal) {
      return Math.max(0, totalPaid - grandTotal - paidOut).toFixed(2);
    }
    return "0.00";
  };

  // Define product and setProductGroups using useState

  const [design, setDesign] = useState(""); // Initialize design state
  const [size, setSize] = useState(""); // Initialize size state
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lType, setLType] = useState(""); // Initialize lType state
  const [lbrPercentage, setLbrPercentage] = useState(0); // Initialize lbrPercentage state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [mrp, setMrp] = useState(0); // Initialize mrp state
  const [image, setImage] = useState(null); // Initialize image state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [productGroup, setProductGroup] = useState(""); // Initialize productGroup state (assuming it's a string)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [discountType, setDiscountType] = useState("₹"); // Discount type (% or ₹)
  const [discountValue, setDiscountValue] = useState(""); // Discount value
  const [roundOffAmount, setRoundOffAmount] = useState(""); // Change to string for input
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [note, setNote] = useState(""); // Add note state

  const getEffectiveRoundOff = () => {
    if (isManualRoundOff) {
      return parseFloat(roundOffAmount) || 0;
    }
    return getAutoPaymentRoundOff();
  };

  const getDisplayRoundOff = () => {
    if (isManualRoundOff) {
      return roundOffAmount;
    }
    return getAutoPaymentRoundOff().toFixed(2);
  };

  // Add resetForm function before handleSubmit
  const resetForm = () => {
    setBillDate(new Date().toISOString().split("T")[0]);
    setCreatedBy(null);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerSaveSuccess(false);
    setBilledTo(true);
    setBilledMobile("");
    setBilledMobileSearchText("");
    setBilledName("");
    setBilledNameSearchText("");
    setBilledAddress("");
    setBilledCity("");
    setBilledSelectedCustomerId(null);
    setBilledSaveSuccess(false);
    setBilledDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
      area: "",
    });
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      // openingBalance: "",
      // pendingBalance: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    setOldPurchases([]);

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset payment rows
    setPaymentRows([
      {
        id: 1,
        pay_mode: "cash", // Pre-selected cash
        type: "",
        remarks: "",
        amount: "0.00",
      },
      {
        id: 2,
        pay_mode: "cheque", // Pre-selected cheque
        type: "",
        remarks: "",
        amount: "0.00",
      },
      {
        id: 3,
        pay_mode: "bank_transfer", // Pre-selected bank transfer
        type: "",
        remarks: "",
        amount: "0.00",
      },
    ]);

    // Reset other fields
    setDiscountType("₹");
    setDiscountValue("");
    const gstBillType =
      billTypes.find((item) => item.name.toLowerCase() === "gst") ||
      billTypes[0];
    if (gstBillType) {
      applyBillTypeSelection(gstBillType);
    } else {
      setSelectedBillTypeId("");
      setSelectedBillTypeName("GST");
      setGstPercentage("3");
    }
    setRoundOffAmount("");
    setIsManualRoundOff(false);
    setNote("");
    setFollowUpDate("");
    setApplyDeliveryToday(true);
    setPerItemDeliveryToday({});
    saleCreateIdempotencyKeyRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const itemErrors = collectSaleItemFieldErrors(tableData);
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      // Validate required fields
      // if (!customerMobile) {
      //   toast.error("Please select a customer");
      //   setIsSubmitting(false);
      //   return;
      // }

      const mobileToSearch = customerMobileSearchText || customerMobile;
      const nameToUse = customerNameSearchText || customerName;
      const mobileError = getMobileValidationError(mobileToSearch, {
        required: true,
      });
      if (mobileError) {
        toast.error(mobileError);
        setIsSubmitting(false);
        return;
      }

      const emailError = getEmailValidationError(customerDetails?.email);
      if (emailError) {
        toast.error(emailError);
        setIsSubmitting(false);
        return;
      }

      if (!createdBy?.value) {
        toast.error("Please select Created By");
        setIsSubmitting(false);
        return;
      }

      const isRealCustomerId = (id) =>
        id != null && id !== "" && !String(id).startsWith("TEMP_");

      // Prefer customer picked from search (already has ERP id)
      let selectedUser = null;
      if (isRealCustomerId(selectedCustomerId)) {
        selectedUser = userss.find(
          (c) => String(c.id) === String(selectedCustomerId),
        );
        if (!selectedUser) {
          selectedUser = {
            id: selectedCustomerId,
            mobile: mobileToSearch,
            fullName: nameToUse,
            name: nameToUse,
            email: customerDetails.email || "",
            state: customerDetails.state || "Gujarat",
            area: customerDetails.area || "",
            city: customerCity || "Ahmedabad",
            address: customerAddress || "",
            aadharNo: customerDetails.aadharNo || "",
            panNo: customerDetails.panNo || "",
            birthDate: customerDetails.birthDate || "",
            maritalStatus: customerDetails.maritalStatus || "",
            anniversaryDate: customerDetails.anniversaryDate || "",
            balance: customerDetails.balance || "",
            saleBalance: customerDetails.saleBalance || "",
            companyName: customerDetails.companyName || "",
            companyAddress: customerDetails.companyAddress || "",
            gstNumber: customerDetails.gstNumber || "",
            reference: customerDetails.reference || "",
          };
        }
      }

      if (!selectedUser) {
        selectedUser = userss.find(
          (c) => String(c.mobile).trim() === String(mobileToSearch).trim(),
        );
      }

      // New customer typed manually (no ERP id yet)
      if (!selectedUser && mobileToSearch && nameToUse) {
        // Create a temporary user object from the form state
        const tempId = `TEMP_${Date.now()}`;
        selectedUser = {
          id: tempId,
          mobile: mobileToSearch,
          fullName: nameToUse,
          name: nameToUse,
          email: customerDetails.email || "",
          state: customerDetails.state || "Gujarat",
          area: customerDetails.area || "",
          city: customerCity || "Ahmedabad",
          address: customerAddress || "",
          aadharNo: customerDetails.aadharNo || "",
          panNo: customerDetails.panNo || "",
          birthDate: customerDetails.birthDate || "",
          maritalStatus: customerDetails.maritalStatus || "",
          anniversaryDate: customerDetails.anniversaryDate || "",
          balance: customerDetails.balance || "",
          saleBalance: customerDetails.saleBalance || "",
          companyName: customerDetails.companyName || "",
          companyAddress: customerDetails.companyAddress || "",
          gstNumber: customerDetails.gstNumber || "",
          reference: customerDetails.reference || "",
        };
      }

      if (!selectedUser) {
        toast.error("Invalid customer selected");
        setIsSubmitting(false);
        return;
      }

      // Format dates
      const formattedDate = billDate
        ? new Date(billDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];
      const formattedFollowupDate = followUpDate
        ? new Date(followUpDate).toISOString().split("T")[0]
        : null;
      const todayDeliveryDate = new Date().toISOString().split("T")[0];

      if (hasItemErrors) {
        setIsSubmitting(false);
        return;
      }

      // Process sales line items for POST /api/sales (type: create)
      const apiLineItems = [];
      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          if (cell.id === "totalAmt" && cell.totalAmountDetails?.gst) {
            acc.totalAmtGst = cell.totalAmountDetails.gst;
          }
          return acc;
        }, {});

        // Skip empty rows
        if (!rowData.productGroup && !rowData.design) {
          continue;
        }

        // Find product group (validates row; ERP sale lines use product_id)
        const selectedProductGroup = await resolveProductGroupForSubmit(
          rowData,
          tableData[rowIndex],
        );
        if (!selectedProductGroup) {
          throw new Error(`Invalid product group in row ${rowIndex + 1}`);
        }

        const itemLevelDelivery =
          rowData.deliveryDate || rowData.delivery_date || "";
        const tagNo = (rowData.tagNo && rowData.tagNo.toString().trim()) || "";
        const isPerItemSelected = tagNo ? !!perItemDeliveryToday[tagNo] : false;
        const deliveryDateForItem = itemLevelDelivery
          ? itemLevelDelivery
          : applyDeliveryToday
            ? todayDeliveryDate
            : isPerItemSelected
              ? todayDeliveryDate
              : "";

        let productId = tableData[rowIndex]?.erpProductId;
        if (!productId && tagNo) {
          try {
            const tagRes = await showProducts({ tag_no: tagNo });
            const p = extractProductFromShowResponse(tagRes, tagNo);
            if (isErpApiSuccess(tagRes) && p?.id != null) {
              productId = p.id;
            }
          } catch (tagErr) {
            console.warn("Could not resolve product_id for tag", tagNo, tagErr);
          }
        }

        const othAmtCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "othAmt",
        );
        const charges = mapOtherAmountEntriesForCreate(
          othAmtCell?.otherAmountEntries || [],
        );

        const line = {
          product_id: productId ? Number(productId) : null,
          product_group_id: selectedProductGroup?.value || null,
          tag_no: tagNo,
          pcs: Number(rowData.pcs),
          gwt: Number(rowData.grWt),
          lwt: Number(rowData.lsWt) || 0,
          nwt: Number(rowData.netWt),
          item_rate_on: mapItemRateOn(rowData.itRateOn),
          item_rate: Number(rowData.itRate) || 0,
          item_amount: Number(rowData.itAmt) || 0,
          lbr_type: mapLbrTypeForApi(rowData.lType),
          lbr_percent: Number(rowData.lbrPercentage) || 0,
          lbr_rate: Number(rowData.lbrRate) || 0,
          lbr_amt: Number(rowData.lbrAmt) || 0,
          // Same as AddPurchase.jsx apiItems: explicit line other amount + charges[]
          oth_amt: Number(rowData.othAmt) || 0,
          total_amount: Number(rowData.totalAmt) || 0,
          charges,
        };
        if (deliveryDateForItem) {
          line.delivery_date = deliveryDateForItem;
        }
        apiLineItems.push(line);
      }

      if (apiLineItems.length === 0) {
        toast.error("No valid product entries to submit");
        setIsSubmitting(false);
        return;
      }

      // Line totals are rounded per row in TotalAmt; footer is their sum.
      // Process payments (standard ERP shape)
      const paymentPersistKeys = [];
      const validPayments = buildStandardPaymentLines(paymentRows, {
        isUpdate: false,
        paymentTypes,
        payModeLabels: PAYMENT_METHOD_LABELS,
        createKey: createSalesIdempotencyKey,
        persistKeys: paymentPersistKeys,
        excludeAdvance: true,
      });

      const parsePaymentAmount = (v) => {
        const n = parseFloat(
          v === "" || v === null || v === undefined ? NaN : v,
        );
        return Number.isFinite(n) ? n : 0;
      };

      const validPayables = payableRows
        .filter((row) => {
          const amt = parsePaymentAmount(row.amount);
          return row.pay_mode && String(row.pay_mode).trim() !== "" && amt > 0;
        })
        .map((row) => {
          const numericType = Number(row.type);
          const bankAccountId =
            Number.isFinite(numericType) && numericType > 0
              ? numericType
              : undefined;

          return {
            bank_account_id: bankAccountId,
            amount: parsePaymentAmount(row.amount),
            payment_mode: String(
              PAYMENT_METHOD_LABELS[row.pay_mode] || row.pay_mode || "",
            )
              .trim()
              .toUpperCase()
              .replace(/\s+/g, "_"),
            remarks: row.remarks || "",
            idempotency_key:
              row.paymentIdempotencyKey || createSalesIdempotencyKey(),
          };
        });

      if (paymentPersistKeys.length > 0) {
        setPaymentRows((prev) => {
          const next = [...prev];
          paymentPersistKeys.forEach(({ rowId, key }) => {
            const idx = next.findIndex((r) => r.id === rowId);
            if (idx >= 0) {
              next[idx] = { ...next[idx], paymentIdempotencyKey: key };
            }
          });
          return next;
        });
      }

      const gstAmts = calculateGstAmount();
      const grandTotal = Number(calculateGrandTotal()) || 0;

      // Make sure queue entry is still available (prevents double creation)
      if (queueDetailsId) {
        try {
          const verifyResponse = await showOrderQueue({});
          const verifyList = Array.isArray(verifyResponse?.data)
            ? verifyResponse.data
            : Array.isArray(verifyResponse?.data?.data)
              ? verifyResponse.data.data
              : [];
          const queueEntry = verifyList.find(
            (item) => String(item.id) === String(queueDetailsId),
          );
          if (
            queueEntry &&
            (!isZeroOrNullValue(queueEntry.sale_id) ||
              !isZeroOrNullValue(queueEntry.custom_order_id) ||
              !isZeroOrNullValue(queueEntry.repairing_id))
          ) {
            toast.error("This order was already processed by another user");
            navigate("/orderqueue");
            return;
          }
        } catch (verifyErr) {
          console.warn("Queue verification failed", verifyErr);
        }
      }

      // Check if this is a new customer (temp ID)
      const isNewCustomer =
        selectedUser.id &&
        String(selectedUser.id).toString().startsWith("TEMP_");

      let newUserData = [];
      let userIdToSend = selectedUser.id;

      if (isNewCustomer) {
        userIdToSend = null;
      }

      // Populate newUserData only if it's a new customer OR if customer details were updated
      if (isNewCustomer || customerSaveSuccess) {
        newUserData = [
          {
            name: selectedUser.name || selectedUser.fullName,
            mobile: selectedUser.mobile,
            email: selectedUser.email,
            city: selectedUser.city,
            address: selectedUser.address,
            aadhar_no: selectedUser.aadharNo,
            pan_no: selectedUser.panNo,
            birth_date: selectedUser.birthDate || "0000-00-00",
            marital_status: selectedUser.maritalStatus,
            anniversary_date: selectedUser.anniversaryDate || "0000-00-00",
            balance: selectedUser.balance,
            sale_balance: selectedUser.saleBalance,
            company_name: selectedUser.companyName,
            company_address: selectedUser.companyAddress,
            GST_number: selectedUser.gstNumber,
            reference: selectedUser.reference,
            state: selectedUser.state,
            area: selectedUser.area || "",
            status: "1",
          },
        ];
      }

      const customerId = userIdToSend;

      if (!saleCreateIdempotencyKeyRef.current) {
        saleCreateIdempotencyKeyRef.current = createSalesIdempotencyKey();
      }

      const createPayload = {
        date: formattedDate,
        sales_user_admin_id: Number(createdBy.value),
        bill_type_id: selectedBillTypeId ? Number(selectedBillTypeId) : null,
        bill_type_name: selectedBillTypeName || "",
        estimate:
          String(selectedBillTypeName || "").toLowerCase() === "quotation"
            ? 1
            : 0,
        discount: Number(discountValue) || 0,
        tax_amount: Number(gstAmts.total) || 0,
        gst_percent: Number(gstPercentage) || 0,
        round_off: getEffectiveRoundOff(),
        grand_total: grandTotal,
        notes: note || "",
        idempotency_key: saleCreateIdempotencyKeyRef.current,
        items: apiLineItems,
        payments: validPayments,
      };
      if (!isNewCustomer && customerId != null && customerId !== "") {
        createPayload.customer_id = Number(customerId);
      }
      if (newUserData.length > 0) {
        createPayload.new_user = newUserData;
      }

      const billedMobileValue = billedTo
        ? selectedUser.mobile || mobileToSearch
        : billedMobileSearchText || billedMobile;
      const billedNameValue = billedTo
        ? selectedUser.name || selectedUser.fullName || nameToUse
        : billedNameSearchText || billedName;
      const billedToCustomerId = billedTo ? customerId : billedSelectedCustomerId;
      const billedToIsExisting =
        billedToCustomerId != null &&
        billedToCustomerId !== "" &&
        isRealCustomerId(billedToCustomerId);

      if (!billedTo && !billedToIsExisting) {
        const billedMobileError = getMobileValidationError(billedMobileValue, {
          required: true,
        });
        if (billedMobileError) {
          toast.error(billedMobileError);
          setIsSubmitting(false);
          return;
        }
        if (!String(billedNameValue || "").trim()) {
          toast.error("Please enter the billed to name");
          setIsSubmitting(false);
          return;
        }
      }

      if (billedToIsExisting) {
        createPayload.billed_to_id = Number(billedToCustomerId);
      } else {
        createPayload.billed_to = [
          {
            name: billedNameValue || "",
            mobile: billedMobileValue || "",
            address: billedTo
              ? selectedUser.address || customerAddress || ""
              : billedAddress || "",
            area: billedTo
              ? selectedUser.area || customerDetails.area || ""
              : billedDetails.area || "",
            state: billedTo
              ? selectedUser.state || customerDetails.state || "Gujarat"
              : billedDetails.state || "Gujarat",
            city: billedTo
              ? selectedUser.city || customerCity || "Ahmedabad"
              : billedCity || "Ahmedabad",
          },
        ];
      }
      if (oldPurchases.length > 0) {
        createPayload.old_metal_details = oldPurchases;
      }
      if (validPayables.length > 0) {
        createPayload.sale_payable = validPayables;
      }
      if (formattedFollowupDate) {
        createPayload.followup_date = formattedFollowupDate;
      }
      if (
        queueDetailsId !== undefined &&
        queueDetailsId !== null &&
        queueDetailsId !== ""
      ) {
        createPayload.queue_details_id = queueDetailsId;
        if (customOrderData?.by_user != null) {
          createPayload.queued_by_user = customOrderData.by_user;
        }
      }
      if (
        isFromRepairingOrder &&
        repairingOrderId !== undefined &&
        repairingOrderId !== null &&
        repairingOrderId !== ""
      ) {
        createPayload.repairing_id = repairingOrderId;
      } else if (
        !isFromRepairingOrder &&
        customOrderId !== undefined &&
        customOrderId !== null &&
        customOrderId !== "" &&
        customOrderId !== 0 &&
        customOrderId !== "0"
      ) {
        createPayload.custom_order_id = customOrderId;
      }

      const hasItemImages = tableData.some((row) =>
        row?.data?.some((cell) => cell?.id === "image" && cell?.imageFile),
      );
      if (hasItemImages) {
        toast(
          "Line images are not uploaded with the new sale API; save stock images from Stock first.",
          { icon: "ℹ️" },
        );
      }
      console.log("Submitting sale with payload:", createPayload);
      const response = await createSale(createPayload);

      if (isSalesApiSuccess(response)) {
        saleCreateIdempotencyKeyRef.current = null;
        trackEvent("Invoice", "Create", getTenantName());
        toast.success("Sales submitted successfully");
        window.dispatchEvent(new CustomEvent(ERP_SALES_LIST_INVALIDATE_EVENT));
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          resetForm();
          navigate("/invoice");
        }, 800);
      } else {
        console.error("API Error:", response?.message || "Operation failed");
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit the form. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!desktopEnv.isDesktop) return undefined;

    const executeSubmit = () => {
      handleSubmit({ preventDefault: () => { } });
    };

    const offSalesSave = commandBus.register({
      id: "sales.save",
      title: "Save Sales Entry",
      category: "Sales",
      run: () => executeSubmit(),
    });
    const offSalesCancel = commandBus.register({
      id: "sales.cancel",
      title: "Cancel Sales Entry",
      category: "Sales",
      run: () => navigate("/invoice"),
    });

    const onFormSave = () => executeSubmit();
    const onFormSaveAndNew = () => executeSubmit();

    const offBarcode = desktopBus.on(
      DesktopEvents.BARCODE_SCANNED,
      (payload) => {
        const barcodeValue = String(payload?.code || "").trim();
        if (!barcodeValue) return;
        const input = document.querySelector("input[placeholder*='tag' i]");
        if (!input) return;
        input.focus();
        input.value = barcodeValue;
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(
          new KeyboardEvent("keydown", { key: "Enter", bubbles: true }),
        );
      },
    );

    window.addEventListener("desktop:form-save", onFormSave);
    window.addEventListener("desktop:form-save-new", onFormSaveAndNew);

    return () => {
      offSalesSave();
      offSalesCancel();
      offBarcode();
      window.removeEventListener("desktop:form-save", onFormSave);
      window.removeEventListener("desktop:form-save-new", onFormSaveAndNew);
    };
  }, [desktopEnv.isDesktop, navigate, location.pathname, isSubmitting]);

  // New function to handle labor calculations
  const handleLaborCalculations = (tableData, rowIndex) => {
    const lTypeCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lType",
    );
    const lbrPercentageCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrPercentage",
    );
    const lbrRateCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrRate",
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt",
    );
    if (lbrAmtCell?.isManual) {
      return;
    }
    const productGroupCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup",
    );

    const lType = (lTypeCell?.value || "").toString().trim();
    const lTypeUpper = lType.toUpperCase(); // Normalize for comparison
    const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
    const lbrRate = parseFloat(lbrRateCell?.value) || 0;
    const itRateCellRef = tableData[rowIndex].data.find(
      (cell) => cell.id === "itRate",
    );
    const itRatePerGram = parseFloat(itRateCellRef?.value) || 0;

    // If the labor type is Fixed, clear the percentage and rate fields
    if (lTypeUpper === "FIXED") {
      if (lbrPercentageCell) lbrPercentageCell.value = "";
      if (lbrRateCell) lbrRateCell.value = "";
      return; // No need to calculate anything for Fixed type
    }

    // Determine the base weight based on labor type (case-insensitive)
    let baseWeight = 0;
    if (lTypeUpper === "NWT") {
      const netWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "netWt",
      );
      baseWeight = parseFloat(netWtCell?.value) || 0;
    } else if (lTypeUpper === "GWT") {
      const grWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "grWt",
      );
      baseWeight = parseFloat(grWtCell?.value) || 0;
    } else if (lTypeUpper === "PCS") {
      const pcsCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "pcs",
      );
      baseWeight = parseFloat(pcsCell?.value) || 0;
    }

    // Calculate labor amount based on which field was last changed
    let lbrAmt = 0;
    let lastChangedField = tableData[rowIndex].lastChangedLaborField;

    // Don't auto-switch fields based on values - respect the user's last edit
    // Only clear the lastChangedField if both values become 0
    if (lbrPercentage === 0 && lbrRate === 0) {
      delete tableData[rowIndex].lastChangedLaborField;
      lastChangedField = null;
    }

    if (lastChangedField === "lbrPercentage") {
      // Calculate based on percentage
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          itRatePerGram,
        );
        // Update labor rate based on percentage of itRate
        if (lbrRateCell) {
          lbrRateCell.value = ((lbrPercentage / 100) * itRatePerGram).toFixed(
            2,
          );
        }
      } else {
        // If percentage is 0 or empty, clear the rate and amount
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
        lbrAmt = 0;
      }
    } else if (lastChangedField === "lbrRate") {
      // Calculate based on rate
      if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        // Update labor percentage based on rate
        if (lbrPercentageCell) {
          lbrPercentageCell.value = itRatePerGram
            ? ((lbrRate / itRatePerGram) * 100).toFixed(2)
            : "";
        }
      } else {
        // If rate is 0 or empty, clear the percentage and amount
        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        lbrAmt = 0;
      }
    } else if (!lastChangedField) {
      // Tag load / lType change: prefer % → fill lbrRate; else rate → fill %
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          itRatePerGram,
        );
        if (lbrRateCell) {
          lbrRateCell.value = ((lbrPercentage / 100) * itRatePerGram).toFixed(
            2,
          );
        }
      } else if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        if (lbrPercentageCell) {
          lbrPercentageCell.value = itRatePerGram
            ? ((lbrRate / itRatePerGram) * 100).toFixed(2)
            : "";
        }
      }
    }

    // Update the labor amount cell
    if (lbrAmtCell) {
      lbrAmtCell.value = Math.round(lbrAmt).toString();
      // Recalculate total amount
      calculateTotalAmount(tableData, rowIndex);
    }
  };

  const [paymentTypes, setPaymentTypes] = useState([]);

  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);
  /** Same key for retries after network errors until a sale succeeds */
  const saleCreateIdempotencyKeyRef = useRef(null);

  // Initialize payment rows state with three common payment methods
  const [paymentRows, setPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash", // Pre-selected cash
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 2,
      pay_mode: "cheque", // Pre-selected cheque
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 3,
      pay_mode: "bank_transfer", // Pre-selected bank transfer
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  // Keep latest URD opener so PaymentSection never sees stale empty customer fields.
  openUrdFromPaymentRowRef.current = (row) => {
    const customer =
      selectedCustomerId != null
        ? userss.find((c) => String(c.id) === String(selectedCustomerId)) ||
        null
        : null;
    const resolvedMobile = String(
      customer?.mobile || customerMobile || customerMobileSearchText || "",
    ).trim();
    const resolvedName = String(
      customer?.fullName ||
      customer?.name ||
      customerName ||
      customerNameSearchText ||
      "",
    ).trim();
    const isTempCustomer =
      selectedCustomerId != null &&
      String(selectedCustomerId).startsWith("TEMP_");
    const isRealCustomer =
      selectedCustomerId != null &&
      selectedCustomerId !== "" &&
      !String(selectedCustomerId).startsWith("TEMP_");

    if (!isRealCustomer && !(resolvedMobile && resolvedName)) {
      toast.error("Please select a customer before adding Old Gold/Silver URD");
      return;
    }

    if (!isRealCustomer) {
      const mobileError = getMobileValidationError(resolvedMobile, {
        required: true,
      });
      if (mobileError) {
        toast.error(mobileError);
        return;
      }
    }

    let draftCustomerId = selectedCustomerId;
    if (!isRealCustomer && !isTempCustomer) {
      draftCustomerId = `TEMP_${Date.now()}`;
      const newCustomerData = {
        id: draftCustomerId,
        mobile: resolvedMobile,
        fullName: resolvedName,
        name: resolvedName,
        email: customerDetails?.email || "",
        state: customerDetails?.state || "",
        city: customerCity || "",
        address: customerAddress || "",
        area: customerDetails?.area || "",
        aadharNo: customerDetails?.aadharNo || "",
        panNo: customerDetails?.panNo || "",
        birthDate: customerDetails?.birthDate || "",
        maritalStatus: customerDetails?.maritalStatus || "",
        anniversaryDate: customerDetails?.anniversaryDate || "",
        balance: customerDetails?.balance || "0",
        saleBalance: customerDetails?.saleBalance || "0",
        companyName: customerDetails?.companyName || "",
        companyAddress: customerDetails?.companyAddress || "",
        gstNumber: customerDetails?.gstNumber || "",
        reference: customerDetails?.reference || "",
      };
      setUserss((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const index = list.findIndex(
          (u) =>
            String(u.id) === String(draftCustomerId) ||
            String(u.mobile || "").trim() === resolvedMobile,
        );
        if (index >= 0) {
          const updated = [...list];
          updated[index] = { ...updated[index], ...newCustomerData };
          return updated;
        }
        return [...list, newCustomerData];
      });
      setSelectedCustomerId(draftCustomerId);
      setCustomerMobile(resolvedMobile);
      setCustomerMobileSearchText(resolvedMobile);
      setCustomerName(resolvedName);
      setCustomerNameSearchText(resolvedName);
    }

    const mergedPaymentRows = ensurePaymentRowInList(paymentRows, row);
    setPaymentRows(mergedPaymentRows);

    setUrdModalState({
      isOpen: true,
      payMode: row.pay_mode || "old_gold",
      row,
    });
  };

  const handleSaveUrdPurchase = (urdData) => {
    setPaymentRows((prev) => {
      return prev.map((r) => {
        if (
          (urdData.paymentRowId != null && r.id === urdData.paymentRowId) ||
          r.pay_mode === urdData.pay_mode
        ) {
          return {
            ...r,
            amount: Number(urdData.grand_total).toFixed(2),
            urd_data: urdData,
          };
        }
        return r;
      });
    });

    setOldPurchases((prev) => {
      const filtered = prev.filter(
        (p) =>
          p.paymentRowId !== urdData.paymentRowId &&
          p.pay_mode !== urdData.pay_mode,
      );
      return [...filtered, urdData];
    });

    setUrdModalState((prev) => ({ ...prev, isOpen: false }));
    toast.success("URD purchase details saved");
  };

  // Ensure payment rows are never empty
  useEffect(() => {
    if (!paymentRows || paymentRows.length === 0) {
      setPaymentRows([
        {
          id: 1,
          pay_mode: "cash",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 2,
          pay_mode: "cheque",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 3,
          pay_mode: "bank_transfer",
          type: "",
          remarks: "",
          amount: "0.00",
        },
      ]);
    }
  }, [paymentRows]);

  // Auto-select types for initial rows when bank accounts are loaded
  useEffect(() => {
    if (bankAccounts.length > 0) {
      const findAccount = (keyword) =>
        bankAccounts.find(
          (b) =>
            (b.accountType &&
              (b.accountType.toLowerCase() === keyword ||
                b.accountType.toLowerCase() === keyword.replace("_", " "))) ||
            (b.bankName &&
              b.bankName.toLowerCase().includes(keyword.replace("_", " "))),
        );

      const cashAccount = findAccount("cash");
      const walletAccount = findAccount("wallet");
      const oldGoldAccount = findAccount("old_gold");
      const oldSilverAccount = findAccount("old_silver");

      const updateRows = (rows) =>
        rows.map((row) => {
          if (!row.type) {
            if (row.pay_mode === "cash" && cashAccount) {
              return { ...row, type: cashAccount.id };
            }
            if (row.pay_mode === "wallet" && walletAccount) {
              return { ...row, type: walletAccount.id };
            }
          }
          return row;
        });

      setPaymentRows((prevRows) => updateRows(prevRows));
      setPayableRows((prevRows) => updateRows(prevRows));
    }
  }, [bankAccounts]);

  useEffect(() => {
    if (bankAccounts.length > 0) {
      const findAccount = (keyword) =>
        bankAccounts.find(
          (b) =>
            (b.accountType &&
              (b.accountType.toLowerCase() === keyword ||
                b.accountType.toLowerCase() === keyword.replace("_", " "))) ||
            (b.bankName &&
              b.bankName.toLowerCase().includes(keyword.replace("_", " "))),
        );

      setPaymentRows((prevRows) => {
        let changed = false;
        const updated = prevRows.map((row) => {
          if (!row.type && ["cash", "wallet"].includes(row.pay_mode)) {
            const account = findAccount(row.pay_mode);
            if (account) {
              changed = true;
              return { ...row, type: account.id };
            }
          }
          return row;
        });
        return changed ? updated : prevRows;
      });
    }
  }, [paymentRows, bankAccounts]);

  const handlePaymentTypeChange = (type) => {
    setPaymentTypes({
      ...paymentTypes,
      [type]: !paymentTypes[type],
    });
  };

  // Close payment dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [paymentDropdownRef]);

  const getSelectedPaymentTypes = () => {
    const selected = Object.entries(paymentTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type, _]) => {
        // Format the payment type label
        if (type === "bankTransfer") return "Bank Transfer";
        return type.charAt(0).toUpperCase() + type.slice(1);
      });

    return selected.length > 0 ? selected.join(", ") : "Select Payment Type";
  };

  // Payment details state
  const [paymentDetails, setPaymentDetails] = useState({
    cash: { amount: "" },
    card: { amount: "" },
    upi: { amount: "" },
    bankTransfer: { amount: "" },
    other: { kashar: "" },
  });

  const handlePaymentDetailChange = (type, field, value) => {
    setPaymentDetails({
      ...paymentDetails,
      [type]: {
        ...paymentDetails[type],
        [field]: value,
      },
    });
  };

  // Payment completion tracking state
  const [followUpDate, setFollowUpDate] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");
  // Delivery date flag (applies to all items unless item-level override is provided)
  const [applyDeliveryToday, setApplyDeliveryToday] = useState(true);
  // Per-item delivery selection when global is OFF (keyed by tag or fallback key)
  const [perItemDeliveryToday, setPerItemDeliveryToday] = useState({});

  // Calculate total amount from payment rows
  const calculatePaymentTotal = () => {
    return paymentRows
      .reduce((sum, row) => {
        const amount = parseFloat(row.amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  };

  // Calculate remaining / outstanding amount
  const calculateRemainingAmount = () => {
    const grandTotal =
      parseFloat(calculateGrandTotal().replace(/[^\d.-]/g, "")) || 0;
    const paymentTotal = parseFloat(calculatePaymentTotal()) || 0;

    if (paymentTotal > grandTotal) {
      const payable = paymentTotal - grandTotal;
      const paidOut = payableRows.reduce(
        (sum, row) => sum + (parseFloat(row.amount) || 0),
        0,
      );
      return Math.max(0, payable - paidOut).toFixed(2);
    }

    const remaining = grandTotal - paymentTotal;
    return Math.max(0, remaining).toFixed(2);
  };

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle customer state change
  const handleCustomerStateChange = (e) => {
    setCustomerDetails({
      ...customerDetails,
      state: e.target.value,
    });
  };

  // Handle round-off amount change (any edit locks manual mode; empty = no round off)
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(
      e,
      (value) => {
        setIsManualRoundOff(true);
        setRoundOffAmount(value);
      },
      {
        allowNegative: true,
        simple: true,
      },
    );
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  useEffect(() => {
    const urdPayment = location.state?.urdPayment;
    if (!urdPayment?.urd_document_id) return;

    const draftKey = location.state?.saleDraftKey;
    let draftPaymentRows = null;
    let draftCustomerSnapshot = null;
    if (draftKey) {
      const draft = loadSaleDraft(draftKey);
      if (draft) {
        draftPaymentRows = draft.paymentRows;
        draftCustomerSnapshot = {
          selectedCustomerId: draft.selectedCustomerId,
          customerMobile: draft.customerMobile,
          customerMobileSearchText: draft.customerMobileSearchText,
          customerName: draft.customerName,
          customerNameSearchText: draft.customerNameSearchText,
        };
        if (draft.tableData) setTableData(draft.tableData);
        if (draft.selectedCustomerId != null) {
          setSelectedCustomerId(draft.selectedCustomerId);
        }
        if (draft.customerMobile != null)
          setCustomerMobile(draft.customerMobile);
        if (draft.customerMobileSearchText != null) {
          setCustomerMobileSearchText(draft.customerMobileSearchText);
        }
        if (draft.customerName != null) setCustomerName(draft.customerName);
        if (draft.customerNameSearchText != null) {
          setCustomerNameSearchText(draft.customerNameSearchText);
        }
        if (draft.note != null) setNote(draft.note);
        if (draft.roundOffAmount != null) {
          setRoundOffAmount(draft.roundOffAmount);
        }
        if (draft.isManualRoundOff != null) {
          setIsManualRoundOff(draft.isManualRoundOff);
        } else if (
          draft.roundOffAmount != null &&
          draft.roundOffAmount !== ""
        ) {
          setIsManualRoundOff(true);
        }
        if (draft.gstPercentage != null) setGstPercentage(draft.gstPercentage);
        if (draft.discountValue != null) setDiscountValue(draft.discountValue);
        if (draft.selectedBillTypeId != null) {
          const billTypeId = String(draft.selectedBillTypeId);
          selectedBillTypeIdRef.current = billTypeId;
          setSelectedBillTypeId(billTypeId);
        }
        if (draft.selectedBillTypeName != null) {
          setSelectedBillTypeName(
            String(draft.selectedBillTypeName || "").trim() || "GST",
          );
        }
        saleDraftKeyRef.current = draftKey;
      }
      clearSaleDraft(draftKey);
    }

    const returnedCustomerId = Number(
      urdPayment.customer_id ?? urdPayment.customerId,
    );
    if (Number.isFinite(returnedCustomerId) && returnedCustomerId > 0) {
      const resolvedMobile = String(
        draftCustomerSnapshot?.customerMobile ||
        draftCustomerSnapshot?.customerMobileSearchText ||
        customerMobile ||
        customerMobileSearchText ||
        "",
      ).trim();
      const resolvedName = String(
        draftCustomerSnapshot?.customerName ||
        draftCustomerSnapshot?.customerNameSearchText ||
        customerName ||
        customerNameSearchText ||
        "",
      ).trim();

      setSelectedCustomerId(returnedCustomerId);
      setCustomerSaveSuccess(false);
      setUserss((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const withoutMatchingTemp = list.filter((u) => {
          const isTemp = String(u?.id ?? "").startsWith("TEMP_");
          if (!isTemp) return true;
          if (!resolvedMobile) return false;
          return String(u.mobile || "").trim() !== resolvedMobile;
        });
        const entry = {
          id: returnedCustomerId,
          fullName: resolvedName,
          name: resolvedName,
          mobile: resolvedMobile,
        };
        const idx = withoutMatchingTemp.findIndex(
          (u) => String(u.id) === String(returnedCustomerId),
        );
        if (idx >= 0) {
          const updated = [...withoutMatchingTemp];
          updated[idx] = { ...updated[idx], ...entry };
          return updated;
        }
        return [...withoutMatchingTemp, entry];
      });
      if (resolvedMobile) {
        setCustomerMobile(resolvedMobile);
        setCustomerMobileSearchText(resolvedMobile);
      }
      if (resolvedName) {
        setCustomerName(resolvedName);
        setCustomerNameSearchText(resolvedName);
      }
    }

    setPaymentRows((prev) =>
      applyUrdLinkToPaymentRows(draftPaymentRows ?? prev, urdPayment),
    );

    toast.success("URD linked to sale payment");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.urdPayment, location.pathname, navigate]);

  const [hasFilledFromCustomOrder, setHasFilledFromCustomOrder] =
    useState(false);
  const [customOrderData, setCustomOrderData] = useState(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);
  const queueHydrationKeyRef = useRef(null);

  const applyCustomerFromQueue = (customer) => {
    if (!customer) return;
    // Set id first so the customer search effect does not fire listCustomerUsers
    setSelectedCustomerId(customer.id);
    setCustomerName(customer.fullName || customer.name || "");
    setCustomerNameSearchText(customer.fullName || customer.name || "");
    setCustomerMobile(customer.mobile || "");
    setCustomerMobileSearchText(customer.mobile || "");
    setCustomerCity(customer.city || "");
    setCustomerAddress(customer.address || "");
    setCustomerDetails((prevDetails) => ({
      ...prevDetails,
      email: customer.email || "",
      birthDate: customer.birthDate || "",
      anniversaryDate: customer.anniversaryDate || "",
      companyName: customer.companyName || "",
      companyAddress: customer.companyAddress || "",
      gstNumber: customer.gstNumber || "",
      aadharNo: customer.aadharNo || "",
      panNo: customer.panNo || "",
      maritalStatus: customer.maritalStatus || "",
      balance: customer.balance || "",
      saleBalance: customer.saleBalance || "",
      reference: customer.reference || "",
      state: customer.state || "",
      area: customer.area || "",
    }));
    if (customer.state) {
      setSelectedState(customer.state);
    }
  };

  const applyQueueOrderToSale = async (foundOrder) => {
    if (!foundOrder) return;
    setHasFilledFromCustomOrder(true);
    setCustomOrderData(foundOrder);

    // Set customer from queue order (single path — skip customerUserId effect)
    const custId =
      foundOrder.customer_user_id ?? foundOrder.user_id ?? customerUserId;
    if (custId) {
      const customer =
        userss.find((user) => String(user.id) === String(custId)) ||
        (await fetchCustomerById(custId));
      applyCustomerFromQueue(customer);
    }

    await fetchProductsFromOrderQueue(foundOrder.products || []);
  };

  const fetchOrderData = async (orderId) => {
    if (!orderId) return;
    setIsLoadingOrderData(true);
    try {
      // Prefer show-by-id (avoids paginated list missing the row)
      let foundOrder = findOrderQueueInResponse(
        await showOrderQueue({ id: orderId }),
        orderId,
      );

      // Fallback: list/search if show-all or show-by-id returns a list without this id
      if (!foundOrder) {
        foundOrder = findOrderQueueInResponse(
          await listOrderQueue({
            search: String(orderId),
            page: 1,
            limit: 50,
          }),
          orderId,
        );
      }

      if (foundOrder) {
        await applyQueueOrderToSale(foundOrder);
      } else {
        console.error("Order not found with ID:", orderId);
        toast.error("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      toast.error("Failed to fetch order data");
    } finally {
      setIsLoadingOrderData(false);
    }
  };

  const fetchProductsFromOrderQueue = async (orderProducts) => {
    try {
      const ids = (orderProducts || [])
        .map((product) => product.pro_id)
        .filter((id) => id != null && id !== "" && id !== 0 && id !== "0");
      if (!ids.length) {
        toast.error("No product IDs in order");
        return;
      }

      const response = await showProducts({
        id: ids.join(","),
      });
      const allProducts = normalizeProductStockShowResponse(response);
      if (allProducts.length === 0) {
        console.warn("No products found in response", response?.data);
        toast.error("No product details found");
        return;
      }
      setAllProducts(allProducts);

      // Ensure full product group details (with metal and purity) are resolved
      const uniquePgIds = [
        ...new Set(
          allProducts
            .map((p) => p.product_group_id ?? p.product_group)
            .filter((id) => id != null && id !== ""),
        ),
      ];
      await Promise.all(
        uniquePgIds.map(async (id) => {
          let option = await resolveProductGroupOption(id);
          if (!option) {
            const productWithName = allProducts.find(
              (p) =>
                String(p.product_group_id ?? p.product_group) === String(id) &&
                p.product_group_name,
            );
            if (productWithName?.product_group_name) {
              const name = productWithName.product_group_name;
              const isGold = /gold|carat|ct|\bkt\b/i.test(name);
              const isSilver = /silver/i.test(name);
              option = {
                value: id,
                label: name,
                name: name,
                metal: isGold ? "Gold" : isSilver ? "Silver" : "",
                purity: "",
              };
              productGroupOptionsRef.current = mergeProductGroupOptions(
                productGroupOptionsRef.current,
                [option],
              );
            }
          }
          if (option) {
            setDropdownData((prev) => ({
              ...prev,
              productGroup: mergeProductGroupOptions(prev.productGroup, [
                option,
              ]),
            }));
          }
        }),
      );

      // Process each product in the order
      const processedProducts = orderProducts
        .map((orderProduct, index) => {
          const product = allProducts.find(
            (p) => String(p.id) === String(orderProduct.pro_id),
          );
          if (product) {
            return {
              id: index + 1,
              erpProductId: product.id,
              erpProductGroupId:
                product.product_group_id ?? product.product_group,
              data: COLUMNS.map((col) => {
                let value = "";
                let extra = {};
                switch (col.id) {
                  case "productGroup": {
                    const pgId =
                      product.product_group_id ?? product.product_group;
                    const group = productGroupOptionsRef.current.find(
                      (pg) => String(pg.value) === String(pgId),
                    );
                    value = group
                      ? group.label || group.name
                      : product.product_group_name || pgId || "";
                    break;
                  }
                  case "design":
                    value = product.design || "";
                    break;
                  case "size":
                    value = product.size || "";
                    break;
                  case "pcs":
                    value = product.pcs || "";
                    break;
                  case "grWt":
                    value = pickProductGrossWeight(product);
                    break;
                  case "lsWt":
                    value =
                      product.lwt_customer ??
                      product.LWt_customer ??
                      product.lwt ??
                      product.LWT ??
                      "";
                    break;
                  case "netWt":
                    value = product.nwt ?? product.NWT ?? "";
                    break;
                  case "itRateOn":
                    value =
                      product.itrateon ||
                      product.item_rate_on ||
                      product.it_rate_on ||
                      orderProduct.itrateon ||
                      orderProduct.item_rate_on ||
                      orderProduct.it_rate_on ||
                      "NWt";
                    break;
                  case "itRate":
                    value =
                      product.itrate ||
                      product.it_rate ||
                      product.rate ||
                      product.item_rate ||
                      orderProduct.itrate ||
                      orderProduct.it_rate ||
                      orderProduct.rate ||
                      orderProduct.item_rate ||
                      orderProduct.price_value ||
                      "";
                    break;
                  case "itAmt":
                    value = product.itamt || orderProduct.itamt || "";
                    break;
                  case "lType":
                    value = product.lbr_type ?? product.LBR_type ?? "NWt";
                    break;
                  case "lbrPercentage":
                    value = product.lbr_percent ?? product.LBR ?? "";
                    break;
                  case "lbrRate":
                    value = product.lbr_rate ?? product.LBR_rate ?? "";
                    break;
                  case "lbrAmt":
                    value = product.lbr_amt ?? product.LBR_amt ?? "";
                    break;
                  case "othAmt": {
                    const othAmt = product.oth_amt ?? product.othamt ?? "";
                    // Map charges to otherAmountEntries with correct field names
                    extra.otherAmountEntries =
                      mapApiChargesToOtherAmountEntries(product.charges);
                    // If charges exist but oth amt is missing/zero, compute value from charges
                    if (
                      extra.otherAmountEntries.length > 0 &&
                      (!othAmt || parseFloat(othAmt) === 0)
                    ) {
                      const total = extra.otherAmountEntries.reduce(
                        (sum, entry) => sum + (parseFloat(entry.amount) || 0),
                        0,
                      );
                      value = total.toFixed(2);
                    } else {
                      value = othAmt || "";
                    }
                    break;
                  }
                  case "mrp":
                    value = product.mrp || "";
                    break;
                  case "image":
                    value = product.image || "";
                    break;
                  case "totalAmt":
                    value = product.totalamt
                      ? roundLineTotalAmount(product.totalamt)
                      : "";
                    break;
                  case "tagNo":
                    value = product.tag_no || "";
                    break;
                  case "huid":
                    value = product.huid || product.HUID || "";
                    break;
                  default:
                    value = "";
                }
                if (col.id === "image") {
                  return {
                    id: col.id,
                    value,
                    imageFile: null,
                    imagePreview: null,
                  };
                }
                if (col.id === "othAmt") {
                  return { id: col.id, value, ...extra };
                }
                return { id: col.id, value };
              }),
            };
          } else {
            return null;
          }
        })
        .filter(Boolean); // Remove null entries

      if (processedProducts.length > 0) {
        let updatedRows = processedProducts;
        for (let i = 0; i < updatedRows.length; i++) {
          const itRateCell = updatedRows[i].data.find((c) => c.id === "itRate");
          const hasExplicitRate =
            itRateCell && parseFloat(itRateCell.value) > 0;
          if (!hasExplicitRate) {
            updatedRows = calculateAndUpdateItRate(updatedRows, i);
          } else {
            // Recompute itAmt from explicit rate & weight
            const itRateOnCell = updatedRows[i].data.find(
              (c) => c.id === "itRateOn",
            );
            const netWtCell = updatedRows[i].data.find(
              (c) => c.id === "netWt",
            );
            const grWtCell = updatedRows[i].data.find(
              (c) => c.id === "grWt",
            );
            const itAmtCell = updatedRows[i].data.find(
              (c) => c.id === "itAmt",
            );
            if (itAmtCell && itRateOnCell && (netWtCell || grWtCell)) {
              const itRateOn = itRateOnCell.value || "NWt";
              const netWt = parseFloat(netWtCell?.value) || 0;
              const grWt = parseFloat(grWtCell?.value) || 0;
              const baseWeight = itRateOn === "GWt" ? grWt : netWt;
              const rate = parseFloat(itRateCell.value) || 0;
              itAmtCell.value = (baseWeight * rate).toFixed(2);
            }
          }

          // Compute labor amount based on lbrPercentage or lbrRate
          const lbrPctVal = parseFloat(
            updatedRows[i].data.find((c) => c.id === "lbrPercentage")?.value ||
              0,
          );
          const lbrRateVal = parseFloat(
            updatedRows[i].data.find((c) => c.id === "lbrRate")?.value || 0,
          );
          if (lbrPctVal > 0) {
            updatedRows[i].lastChangedLaborField = "lbrPercentage";
          } else if (lbrRateVal > 0) {
            updatedRows[i].lastChangedLaborField = "lbrRate";
          }
          handleLaborCalculations(updatedRows, i);

          // Compute line total amount
          calculateTotalAmount(updatedRows, i);
        }

        setTableData(updatedRows);
        setHasFilledFromCustomOrder(true);
      }
    } catch (error) {
      console.error("Error fetching product details for order:", error);
      toast.error("Failed to fetch product details");
    }
  };

  // Load queue order when arriving from Order Queue (READYPRO → Sales)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hasDirectTableRows =
      Array.isArray(location.state?.customOrderTable) &&
      location.state.customOrderTable.length > 0;

    // Custom order / repairing table already sends line rows — do not
    // treat that payload as an order-queue product list (pro_id fetch).
    if (hasDirectTableRows) {
      const seeded = location.state?.customOrderData;
      if (seeded) {
        setCustomOrderData(seeded);
      }
      return;
    }

    const seeded = location.state?.customOrderData;
    const hydrationKey = queueDetailsId
      ? `queue:${queueDetailsId}`
      : seeded?.id != null
        ? `seeded:${seeded.id}`
        : null;
    if (!hydrationKey) return;
    // Guard Strict Mode double-invoke and duplicate customer/product fetches
    if (queueHydrationKeyRef.current === hydrationKey) return;
    queueHydrationKeyRef.current = hydrationKey;

    if (seeded) {
      setIsLoadingOrderData(true);
      applyQueueOrderToSale(seeded).finally(() => setIsLoadingOrderData(false));
      return;
    }
    fetchOrderData(queueDetailsId);
  }, [
    queueDetailsId,
    location.state?.customOrderData,
    location.state?.customOrderTable,
  ]);

  // Recalculate It Rate from live price when master rates change
  useEffect(() => {
    if (!tableData || tableData.length === 0) return;
    setTableData((prev) => {
      let updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        const pgCell = updated[i].data.find((c) => c.id === "productGroup");
        if (!pgCell || !pgCell.value) continue;
        updated = calculateAndUpdateItRate(updated, i);
        const lbrPctVal = parseFloat(
          updated[i].data.find((c) => c.id === "lbrPercentage")?.value || 0,
        );
        const lbrRateVal = parseFloat(
          updated[i].data.find((c) => c.id === "lbrRate")?.value || 0,
        );
        if (lbrPctVal > 0) {
          updated[i].lastChangedLaborField = "lbrPercentage";
        } else if (lbrRateVal > 0) {
          updated[i].lastChangedLaborField = "lbrRate";
        }
        handleLaborCalculations(updated, i);
        calculateTotalAmount(updated, i);
      }
      return updated;
    });
  }, [masterGoldRate, masterSilverRate, showLiveRate]);

  const fillFromCustomOrderState = useMemo(() => {
    const hasDirectTableRows =
      Array.isArray(location.state?.customOrderTable) &&
      location.state.customOrderTable.length > 0;
    if (hasDirectTableRows) {
      return location.state;
    }
    if (customOrderData) {
      return {
        customOrderId: customOrderId,
        customOrderTable: customOrderData.products || [],
        customOrderData: customOrderData,
        customOrderPayments: customOrderData.payments || [],
      };
    }
    return location.state;
  }, [location.state, customOrderData, customOrderId]);

  // Replace the old useEffect with the custom hook
  useFillSalesFromCustomOrder({
    locationState: fillFromCustomOrderState,
    setTableData,
    setPaymentRows,
    setRoundOffAmount: (value) => {
      setRoundOffAmount(value);
      setIsManualRoundOff(true);
    },
    setNote,
    COLUMNS,
    dropdownData,
    hasFilledFromCustomOrder,
    setHasFilledFromCustomOrder,
    userss,
    setCustomerName,
    setCustomerNameSearchText,
    setCustomerMobile,
    setCustomerMobileSearchText,
    setSelectedCustomerId,
    setCustomerDetails,
    setCustomerCity,
    setCustomerAddress,
    setSelectedState,
    states,
    cities,
    allProducts,
    productGroups: dropdownData.productGroup || [],
  });

  // Pre-fill customer when customerUserId is provided (non-queue navigations only)
  useEffect(() => {
    if (!customerUserId) return;
    // Queue → Sales loads the customer inside applyQueueOrderToSale
    if (queueDetailsId) return;

    const customerUser = userss.find(
      (user) => String(user.id) === String(customerUserId),
    );

    if (customerUser) {
      applyCustomerFromQueue(customerUser);
      return;
    }

    fetchCustomerById(customerUserId).then((customer) => {
      applyCustomerFromQueue(customer);
    });
  }, [customerUserId, userss, queueDetailsId]);

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-3 py-2 border-bottom">
            <div className="d-flex align-items-center flex-wrap gap-3 w-100">
              <h4 className="card-title mb-0">Add Sales</h4>
              <div className="d-flex align-items-center flex-shrink-0">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 text-nowrap"
                    style={{
                      minWidth: "74px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Bill Type:
                  </label>
                  <div style={billTypeHeaderSelectWrapStyle}>
                    <Select
                      styles={billTypeHeaderSelectStyles}
                      menuPlacement="bottom"
                      isSearchable={false}
                      options={billTypes
                        .filter(
                          (item) =>
                            String(item.id) !==
                            String(selectedBillTypeId || ""),
                        )
                        .map((item) => ({
                          value: String(item.id),
                          label: item.name,
                        }))}
                      value={(() => {
                        const selected =
                          billTypes.find(
                            (item) =>
                              String(item.id) === String(selectedBillTypeId),
                          ) ||
                          billTypes.find(
                            (item) => item.name.toLowerCase() === "gst",
                          ) ||
                          null;
                        return selected
                          ? {
                            value: String(selected.id),
                            label: selected.name,
                          }
                          : null;
                      })()}
                      onChange={(option) => {
                        const selected = billTypes.find(
                          (item) => String(item.id) === String(option?.value),
                        );
                        applyBillTypeSelection(selected);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Date:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={billDate}
                  onChange={handleDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              {/* Created By */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Sale By:
                </label>
                <div style={createdByHeaderSelectWrapStyle}>
                  <Select
                    styles={createdByHeaderSelectStyles}
                    classNamePrefix="created-by-select"
                    menuPlacement="bottom"
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    isSearchable={true}
                    isClearable={false}
                    isLoading={employeesLoading}
                    options={employeeOptions}
                    value={createdBy}
                    onChange={setCreatedBy}
                    placeholder="Search employee"
                    noOptionsMessage={() => "No employee found"}
                  />
                </div>
              </div>
              {(selectedCustomerId || productGroupPoolHeader) && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  {selectedCustomerId && (
                    <>
                      <span
                        className={`badge ${parseFloat(customerDetails.balance) >= 0
                          ? "bg-success-subtle text-success"
                          : "bg-danger-subtle text-danger"
                          }`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Balance: ₹
                        {parseFloat(customerDetails.balance || 0).toFixed(2)}
                      </span>
                      <span
                        className={`badge ${parseFloat(customerDetails.saleBalance) > 0
                          ? "bg-danger-subtle text-danger"
                          : "bg-success-subtle text-success"
                          } ms-2`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Pending: ₹
                        {parseFloat(customerDetails.saleBalance || 0).toFixed(
                          2,
                        )}
                      </span>
                    </>
                  )}
                  <ProductGroupPoolBalance
                    balance={productGroupPoolHeader}
                    className=""
                  />
                </div>
              )}
            </div>
          </div>
          <div className="card-body py-3 px-3">
            <div className="d-flex align-items-center mb-2">
              <input
                id="sales-billed-to"
                type="checkbox"
                className="form-check-input mt-0"
                checked={billedTo}
                onChange={(e) => setBilledTo(e.target.checked)}
              />
              <label
                htmlFor="sales-billed-to"
                className="form-check-label ms-2 mb-0"
                style={{ fontSize: "0.85rem", fontWeight: 600 }}
              >
                Billed to
              </label>
            </div>
            {!billedTo && (
              <CustomerSelection
                hideLabel
                hideAddButton
                hideStateCity
                customerMobile={billedMobile}
                setCustomerMobile={setBilledMobile}
                customerMobileSearchText={billedMobileSearchText}
                setCustomerMobileSearchText={setBilledMobileSearchText}
                customerName={billedName}
                setCustomerName={setBilledName}
                customerNameSearchText={billedNameSearchText}
                setCustomerNameSearchText={setBilledNameSearchText}
                customerAddress={billedAddress}
                setCustomerAddress={setBilledAddress}
                customerDetails={billedDetails}
                setCustomerDetails={setBilledDetails}
                customerCity={billedCity}
                setCustomerCity={setBilledCity}
                selectedCustomerId={billedSelectedCustomerId}
                setSelectedCustomerId={setBilledSelectedCustomerId}
                customerSaveSuccess={billedSaveSuccess}
                setCustomerSaveSuccess={setBilledSaveSuccess}
                users={userss}
                setUsers={setUserss}
                states={states}
                cities={cities}
                selectedState={billedSelectedState}
                setSelectedState={setBilledSelectedState}
                buttonClass={buttonClass}
                savingCustomer={savingCustomer}
              />
            )}
            <CustomerSelection
              customerMobile={customerMobile}
              setCustomerMobile={setCustomerMobile}
              customerMobileSearchText={customerMobileSearchText}
              setCustomerMobileSearchText={setCustomerMobileSearchText}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerNameSearchText={customerNameSearchText}
              setCustomerNameSearchText={setCustomerNameSearchText}
              customerAddress={customerAddress}
              setCustomerAddress={setCustomerAddress}
              customerDetails={customerDetails}
              setCustomerDetails={setCustomerDetails}
              customerCity={customerCity}
              setCustomerCity={setCustomerCity}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              customerSaveSuccess={customerSaveSuccess}
              setCustomerSaveSuccess={setCustomerSaveSuccess}
              users={userss}
              setUsers={setUserss}
              states={states}
              cities={cities}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              buttonClass={buttonClass}
              savingCustomer={savingCustomer}
              excludeMobile={
                isQueueLocked
                  ? customerMobileSearchText || customerMobile || null
                  : null
              }
            />

            <div
              ref={tableContainerRef}
              className={`table-responsive border ${buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
                } mt-2 ${cardClass}`}
              style={{
                maxHeight:
                  tableData.length > 4
                    ? `${40 + 4 * 34 + 34}px` // header + 4 data rows + totals row
                    : undefined,
                overflowY: tableData.length > 4 ? "auto" : "visible",
              }}
            >
              <table className="table table-bordered table-hover table-sm mb-0 theme-header-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 4 ? "sticky" : undefined,
                          top: tableData.length > 4 ? 0 : undefined,
                          zIndex: tableData.length > 4 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                              onMouseEnter={() =>
                                setHoveredNoRowIndex(rowIndex)
                              }
                              onMouseLeave={() => setHoveredNoRowIndex(null)}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "34px" }}
                              >
                                {hoveredNoRowIndex === rowIndex ? (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center"
                                    onClick={() => handleDeleteRow(rowIndex)}
                                    title={`Delete row ${rowIndex + 1}`}
                                    aria-label={`Delete row ${rowIndex + 1}`}
                                    style={{
                                      color: "#dc3545",
                                      textDecoration: "none",
                                      lineHeight: 1,
                                    }}
                                  >
                                    <Icon
                                      icon="solar:trash-bin-trash-bold"
                                      width={18}
                                      height={18}
                                    />
                                  </button>
                                ) : (
                                  <span style={{ fontSize: "0.85rem" }}>
                                    {rowIndex + 1}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              verticalAlign: "top",
                              backgroundColor: hasFieldError
                                ? "#fff5f5"
                                : undefined,
                              boxShadow: hasFieldError
                                ? "inset 0 0 0 2px #dc3545"
                                : undefined,
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue,
                                    );
                                    // Show dropdown when typing, but for tagNo and productGroup only after 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    // Check if this is a column that requires 3+ characters
                                    const requiresMinLength =
                                      columnType === "tagNo" ||
                                      columnType === "productGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    // For tagNo and productGroup, only show dropdown if 3+ characters
                                    if (
                                      columnType === "tagNo" ||
                                      columnType === "productGroup"
                                    ) {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }
                                    } else {
                                      // For other columns, show dropdown normally
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType,
                                      );
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "34px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                  (fileInputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />
                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "34px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current,
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";
                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300,
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(cell.imagePreview);
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `tooltip-${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current,
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex,
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image",
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(cell.imagePreview);
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current,
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current,
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2,
                                                )}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0,
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "totalAmt" ? (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  handleKeyDown(
                                    e,
                                    rowIndex,
                                    colIndex,
                                    getColumnType(colIndex),
                                  );
                                  handleTotalAmountInteraction(rowIndex, e);
                                }}
                                onDoubleClick={(e) =>
                                  handleTotalAmountInteraction(rowIndex, e)
                                }
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                  const raw = parseFloat(cell.value);
                                  if (cell.value !== "" && !Number.isNaN(raw)) {
                                    const rounded = roundLineTotalAmount(raw);
                                    if (rounded !== String(cell.value)) {
                                      handleInputChange(
                                        rowIndex,
                                        colIndex,
                                        rounded,
                                      );
                                    }
                                  }
                                }}
                                title={`Double-click or press Enter twice to enter amount details${cell?.totalAmountDetails?.gst
                                  ? ` (GST ${cell.totalAmountDetails.gst}%)`
                                  : ""
                                  }`}
                              />
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Special handling for text fields - allow alphanumeric (design, huid)
                                  if (
                                    columnType === "design" ||
                                    columnType === "huid"
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      position: tableData.length > 4 ? "sticky" : undefined,
                      bottom: tableData.length > 4 ? 0 : undefined,
                      zIndex: tableData.length > 4 ? 5 : undefined,
                    }}
                  >
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Tag No */}
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0"></td> {/* Design */}
                    <td className="p-0"></td> {/* Size */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0,
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* GWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const gwt = parseFloat(
                                row.data.find((cell) => cell.id === "grWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(gwt) ? 0 : gwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* LWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lwt = parseFloat(
                                row.data.find((cell) => cell.id === "lsWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(lwt) ? 0 : lwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* NWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const nwt = parseFloat(
                                row.data.find((cell) => cell.id === "netWt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(nwt) ? 0 : nwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* ItRateOn */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItRate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {/* Average item rate - only consider rows with rate > 0 */}
                          {(() => {
                            const validRows = tableData.filter((row) => {
                              const itRate = parseFloat(
                                row.data.find((cell) => cell.id === "itRate")
                                  ?.value || 0,
                              );
                              return itRate > 0;
                            });

                            if (validRows.length === 0) return "0.00";

                            const sum = validRows.reduce((acc, row) => {
                              const itRate = parseFloat(
                                row.data.find((cell) => cell.id === "itRate")
                                  ?.value || 0,
                              );
                              return acc + itRate;
                            }, 0);

                            return (sum / validRows.length).toFixed(2);
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const itAmt = parseFloat(
                                row.data.find((cell) => cell.id === "itAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(itAmt) ? 0 : itAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Lbr Type */}
                    <td className="p-0"></td> {/* Lbr % */}
                    <td className="p-0"></td> {/* Lbr Rate */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Amt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmt = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(lbrAmt) ? 0 : lbrAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* OthAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find((cell) => cell.id === "othAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* HUID */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* MRP */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const mrp = parseFloat(
                                row.data.find((cell) => cell.id === "mrp")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(mrp) ? 0 : mrp);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Image — footer spacer (per-row round-off lives in TotalAmt) */}
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* TotalAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "34px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <PaymentSection
              paymentRows={paymentRows}
              onPaymentRowsChange={setPaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              onOpenUrdFromPaymentRow={openUrdFromPaymentRow}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              showDeliverySection
              applyDeliveryToday={applyDeliveryToday}
              onApplyDeliveryTodayChange={setApplyDeliveryToday}
              deliveryProducts={tableData
                .map((row) => {
                  const tagCell = row.data.find((c) => c.id === "tagNo");
                  const tag = (tagCell?.value || "").toString().trim();
                  if (!tag) return null;
                  return { key: tag, label: tag };
                })
                .filter(Boolean)}
              perItemDeliveryToday={perItemDeliveryToday}
              onPerItemDeliveryTodayChange={setPerItemDeliveryToday}
              finalAmount={calculateTotal().toFixed(2)}
              discountType={discountType}
              onDiscountTypeChange={setDiscountType}
              discountValue={discountValue}
              onDiscountValueChange={setDiscountValue}
              discountAmount={calculateDiscountAmount().toFixed(2)}
              gstPercentage={gstPercentage}
              onGstPercentageChange={setGstPercentage}
              gstAmount={calculateGstAmount().total.toFixed(2)}
              roundOffAmount={getDisplayRoundOff()}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={calculateTotalPaid()}
              payableAmount={calculatePayableAmount()}
              remainingAmount={calculateRemainingAmount()}
              showOutstanding
              outstandingAmount={parseFloat(
                customerDetails.saleBalance || 0,
              ).toFixed(2)}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-3">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <button
                      className={`btn btn-primary ${buttonClass}`}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={sumOtherAmountEntries}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      <OldPurchaseModal
        show={urdModalState.isOpen}
        onClose={() => setUrdModalState((prev) => ({ ...prev, isOpen: false }))}
        onSave={handleSaveUrdPurchase}
        initialData={
          urdModalState.row?.urd_data ||
          oldPurchases.find(
            (p) =>
              (urdModalState.row?.id != null && p.paymentRowId === urdModalState.row?.id) ||
              p.pay_mode === urdModalState.payMode,
          ) ||
          null
        }
        payMode={urdModalState.payMode}
        paymentRowId={urdModalState.row?.id}
        masterGoldRate={masterGoldRate}
        masterSilverRate={masterSilverRate}
        showLiveRate={showLiveRate}
      />
      {renderTotalAmountModal()}
      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default Sales;
